/**
 * Beschaffung und Verpackung einer SKU -- ZWEI Bereiche aus einer Quelle.
 *
 * Beides haengt an derselben Supplier-Beziehung, und genau deshalb standen
 * beide bis V1 in einem gemeinsamen Bereich „Einkauf & Logistik". Das war
 * eine Gliederung nach dem Datenmodell, nicht nach der Sache:
 *
 *   EINKAUF     Fertigungspartner, Supplier-Artikelnummer, MOQ,
 *               Bestellvielfaches, Lieferzeit -- vertraulich.
 *   VERPACKUNG  Kartonmasse, Gewicht, CBM, Produktmasse, Zolltarifnummer --
 *               fuer Vertrieb und Planung nuetzlich und nicht geheim.
 *
 * Wer beides zusammen zeigt, kann das eine nicht freigeben, ohne das andere
 * mitzugeben. Diese Datei traegt deshalb zwei Einstiege auf denselben Daten;
 * das Datenmodell bleibt unveraendert.
 *
 * WICHTIG: die Verpackungsansicht nennt den Lieferanten NICHT. Sie zeigt den
 * Verpackungsstand, nicht dessen Urheber.
 *
 * Diese Ansicht macht sichtbar, was Migration 0012 traegt, und behauptet
 * nichts darueber hinaus:
 *
 *   * CBM und CBM je Stueck werden ANGEZEIGT, niemals eingegeben. Es gibt in
 *     dieser Datei kein Eingabefeld dafuer.
 *   * Fehlt die Masterebene, steht dort „unbekannt" -- nicht 0,000000.
 *   * Eine fehlende Inner Box wird nicht bemaengelt, nicht markiert und nicht
 *     erwaehnt. Sie ist kein Zustand, ueber den die Oberflaeche etwas sagt.
 *   * Historische Revisionen bleiben sichtbar. Der aktuelle Stand ist
 *     erkennbar, weil er die hoechste Revision traegt -- nicht, weil ein
 *     Kennzeichen es behauptet.
 *
 * WAS HIER AUSDRUECKLICH NICHT STEHT: Logistikampel, Effizienzbewertung,
 * Auslastungsgrad, Frachtrate, Verpackungskosten, Bestellung, Liefertermin.
 * Die Kriterien dafuer sind fachlich offen; eine Oberflaeche, die sie
 * vorwegnimmt, waere eine Entscheidung.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  PaymentMethod,
  ArticleCustomsResponse, ArticleLogisticsResponse, CreatePackagingVersionRequest,
  CustomsFact, CustomsFactCode, CustomsFactSet,
  MetaResponse, ModelRef, PackagingLevel, PackagingMaterial, PackagingVersion,
  PaymentTerms, PaymentTermsLevel, PurchasingDetails, ResolvedCustomsValue,
  SupplierArticle, SupplierRating, UpdateSupplierRatingRequest,
} from "@homebase/shared";
import { ebenenbilanz, offeneDifferenzen, PRICE_CURRENCIES, formatCbm, LOGISTICS_METHODS, LOGISTICS_ORIGINS,
         materialJeProdukt, offenerRest, QUALITY_STATUS_CODES, SUPPLIER_RATING_CRITERIA,
         verpackungsbilanz } from "@homebase/shared";
import type { BilanzEbene } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { heuteLokal } from "../lib/datum.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import {
  gesamtbewertung,
  massgeblich, ausZahlplan, zahlplanGleich, zahlplanSatz, Zahlplaneditor,
  type Bewertung, type Zahlentwurf,
} from "./SupplierBits.tsx";
import { Unknown } from "./States.tsx";
import { betrag } from "./ProductDossier.tsx";
import "./ArticleLogistics.css";

/**
 * Der gemeinsame Lader.
 *
 * Beide Bereiche lesen dieselbe Adresse. Sie doppelt zu holen waere kein
 * Fehler, aber zwei Anfragen fuer eine Antwort.
 */
function useLogistics(articleId: number): {
  data: ArticleLogisticsResponse | null;
  error: unknown;
  setData: (d: ArticleLogisticsResponse | null) => void;
  setError: (e: unknown) => void;
  replaceRelation: (r: SupplierArticle) => void;
  reload: () => Promise<void>;
} {
  const [data, setData] = useState<ArticleLogisticsResponse | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    api.articleLogistics(articleId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [articleId]);

  const replaceRelation = (updated: SupplierArticle): void => {
    setData((d) => d === null ? d : {
      ...d,
      supplierArticles: d.supplierArticles.map((r) => r.id === updated.id ? updated : r),
    });
  };
  const reload = async (): Promise<void> => {
    try { setData(await api.articleLogistics(articleId)); } catch (e) { setError(e); }
  };
  return { data, error, setData, setError, replaceRelation, reload };
}

/**
 * EINKAUF -- der geschuetzte Bereich.
 *
 * Die Frage, die dieser Bereich beantwortet, ist nicht „welche Datensaetze
 * haengen an diesem Artikel", sondern: bei wem wird er beschafft, unter
 * welcher Nummer, ab welcher Menge und mit welcher Vorlaufzeit. Was davon
 * fehlt, steht oben -- benannt, nicht gezaehlt.
 */
export function ArticlePurchasingSection({ articleId, meta, mayWrite, mayWritePrice = false,
                                           mayConfirm, attention,
                                           i18nLabel, editing = false, onEditingChange,
                                           steuerung, onOpenSupplier }: {
  /** Ins Lieferantenportal, zu genau diesem Lieferanten -- fehlt, wo es keinen Weg gibt. */
  onOpenSupplier?: (supplierId: number, fokus?: "bewertung") => void;
  articleId: number;
  meta: MetaResponse;
  /** Die Oberflaeche richtet sich danach; entschieden wird im Server. */
  mayWrite: boolean;
  /** `price.input.write` -- Einkaufspreis erfassen, in der Karte. */
  mayWritePrice?: boolean;
  mayConfirm: boolean;
  /** Die offenen Punkte DIESES Bereichs, bereits gefiltert. */
  attention: React.ReactNode;
  i18nLabel: string;
  editing?: boolean;
  onEditingChange?: (v: boolean) => void;
  steuerung?: React.MutableRefObject<LogistikSteuerung | null>;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const { data, error, setError, replaceRelation, reload } = useLogistics(articleId);
  const [adding, setAdding] = useState(false);

  /* ── Der Bearbeitungsmodus ──────────────────────────────────────────────
   *
   * Dieselbe Logik wie in Technik und Logistik -- Entwurf, ein Speichern,
   * derselbe Ausgang. Was hier NICHT dazugehoert, ist der Einkaufspreis:
   * er hat eine eigene Adresse, einen eigenen Gueltigkeitsstand und wird
   * in der Produktakte gepflegt. Ihn hier zusaetzlich editierbar zu machen
   * waere ein zweiter Pflegeweg fuer dieselbe Kondition.
   */
  const [entwurf, setEntwurf] = useState<Record<number, Beziehungsfeld>>({});
  /*
   * DIE LIEFERANTENBEWERTUNG WIRD HIER NICHT MEHR GEPFLEGT.
   *
   * Sie gehoert dem Lieferanten und wird im Lieferantenportal bewertet
   * (dieselbe `BewertungsBox`, derselbe Weg `PUT /suppliers/{id}/rating`).
   * Die Produktakte zeigt nur ihr Ergebnis -- „AGC ★ 4,1" -- und fuehrt mit
   * „Lieferant öffnen" dorthin.
   */
  /*
   * Der Entwurf des Zahlungsplans -- je BEZIEHUNG, mit seiner Ebene.
   *
   * Er haengt an der Beziehung, weil man von hier aus BEIDES bearbeiten
   * kann: den Standard des Lieferanten und die Abweichung fuer genau
   * diesen Artikel. Welches von beidem gemeint ist, sagt `ebene` -- und
   * nicht die Vermutung des Speicherwegs.
   */
  const [zahlplan, setZahlplan] = useState<Record<number, Zahlentwurf>>({});
  const [speichert, setSpeichert] = useState(false);
  const [bericht, setBericht] = useState<{ ok: number; offen: number } | null>(null);

  const stand = (r: SupplierArticle): Beziehungsfeld => ({
    nummer: r.purchasing?.supplierArticleNumber ?? "",
    moq: r.purchasing?.moq == null ? "" : String(r.purchasing.moq),
    vielfaches: r.purchasing?.orderMultiple == null ? "" : String(r.purchasing.orderMultiple),
    lieferzeit: r.purchasing?.leadTimeDays == null ? "" : String(r.purchasing.leadTimeDays),
  });
  const beziehungen = data?.supplierArticles ?? [];
  const offeneBez = beziehungen.filter((r) => {
    const e = entwurf[r.id];
    if (e === undefined) return false;
    const v = stand(r);
    return e.nummer !== v.nummer || e.moq !== v.moq
        || e.vielfaches !== v.vielfaches || e.lieferzeit !== v.lieferzeit;
  });
  const offeneZahlplaene = beziehungen.filter((r) => {
    const e = zahlplan[r.id];
    return e !== undefined && r.purchasing !== null
        && !zahlplanGleich(e, ausZahlplan(r.purchasing));
  });
  const dirty = offeneBez.length > 0 || offeneZahlplaene.length > 0;

  const verwerfen = useCallback((): void => {
    setEntwurf({}); setZahlplan({}); setBericht(null);
  }, []);
  useEffect(() => { if (!editing) verwerfen(); }, [editing, verwerfen]);
  useEffect(() => { verwerfen(); }, [articleId, verwerfen]);

  const speichern = useCallback(async (): Promise<boolean> => {
    if (offeneBez.length === 0 && offeneZahlplaene.length === 0) return true;
    setSpeichert(true); setError(null);
    let ok = 0;
    /*
     * DER ZAHLUNGSPLAN -- als GANZES, auf der gewaehlten Ebene.
     *
     * Keine Stufe mehr heisst: die Vereinbarung wird zurueckgenommen. Sie
     * mit null Stufen zu speichern waere ein Plan, der nichts vereinbart.
     */
    /*
     * DER ENTWURF BLEIBT STEHEN, WENN ER SCHEITERT.
     *
     * Ein Zahlungsplan ist keine einzelne Zahl: wer vier Stufen tippt und
     * die Summe verfehlt, haette sonst vier Stufen verloren und muesste
     * von vorn anfangen. Dieselbe Regel wie bei den Verpackungsebenen.
     */
    const planFehler = new Set<number>();
    for (const r of offeneZahlplaene) {
      const e = zahlplan[r.id]!;
      const weg = e.ebene === "supplier" ? "suppliers" : "supplier-articles";
      const id = e.ebene === "supplier" ? r.purchasing!.supplier.id : r.id;
      try {
        if (e.stufen.length === 0) await api.removePaymentTerms(weg, id);
        else {
          await api.setPaymentTerms(weg, id, {
            stages: e.stufen.map((x) => ({
              sharePercent: x.anteil.trim().replace(",", "."),
              event: x.ereignis,
              eventNote: x.zusatz.trim() === "" ? null : x.zusatz.trim(),
              offsetDays: x.frist.trim() === "" ? null : Number(x.frist.trim()),
              /* Leer heisst „nicht gesagt" und wird zu `null` -- nicht
                 zu einer leeren Zeichenkette, die der Server als
                 Beschriftung aus Leerraum abweisen muesste. */
              label: x.bezeichnung.trim() === "" ? null : x.bezeichnung.trim(),
              paymentMethod: x.weg === "" ? null : (x.weg as PaymentMethod),
            })),
          });
        }
        ok++;
      } catch (e2) { setError(e2); planFehler.add(r.id); }
    }
    /* Eine leere Eingabe heisst „nicht bestimmt" und wird zu `null` --
       nicht zu 0. Null Stueck Mindestbestellmenge waere eine Aussage. */
    const zahl = (v: string): number | null =>
      v.trim() === "" ? null : Number(v.trim());
    for (const r of offeneBez) {
      const e = entwurf[r.id]!;
      try {
        onChangedIntern(await api.updateSupplierArticle(r.id, {
          supplierArticleNumber: e.nummer.trim() === "" ? null : e.nummer.trim(),
          moq: zahl(e.moq), orderMultiple: zahl(e.vielfaches),
          leadTimeDays: zahl(e.lieferzeit),
        }));
        ok++;
      } catch (e2) { setError(e2); }
    }
    const gesamt = offeneBez.length + offeneZahlplaene.length;
    setBericht({ ok, offen: gesamt - ok });
    setEntwurf({});
    setZahlplan((s2) => Object.fromEntries(
      Object.entries(s2).filter(([id]) => planFehler.has(Number(id)))));
    setSpeichert(false);
    await reload();
    return gesamt - ok === 0;
    function onChangedIntern(x: SupplierArticle): void { replaceRelation(x); }
  }, [offeneBez, offeneZahlplaene, entwurf, zahlplan,
      reload, replaceRelation, setError]);

  const speichernRef = useRef(speichern);
  speichernRef.current = speichern;
  useEffect(() => {
    if (!steuerung) return;
    steuerung.current = { dirty, save: () => speichernRef.current(), discard: verwerfen };
    return () => { steuerung.current = null; };
  }, [dirty, verwerfen, steuerung]);

  const bearbeitet = editing && mayWrite;

  return (
    <>
      <h3 className="detail__section">{i18nLabel}</h3>
      {attention}
      {error !== null && <p className="log__error">{errorText(i18n, error)}</p>}

      {bericht !== null && (
        <p className={bericht.offen === 0 ? "log__bericht" : "log__bericht log__bericht--teil"}
           role="status">
          {bericht.offen === 0
            ? t("tk.savedAll", { n: bericht.ok })
            : t("tk.savedSome", { ok: bericht.ok, offen: bericht.offen })}
        </p>
      )}

      {data === null && error === null && <p className="log__note">{t("common.loading")}</p>}

      {data !== null && data.supplierArticles.length === 0 && (
        <p className="log__note">{t("log.noRelations")}</p>
      )}

      {(() => {
        return data?.supplierArticles.map((r) => {
          const l = r.purchasing?.supplier;
          return (
            <PurchasingRelation key={r.id} relation={r} mayWrite={mayWrite}
              mayWritePrice={mayWritePrice} mayConfirm={mayConfirm}
              bearbeitet={bearbeitet} entwurf={entwurf[r.id]} speichert={speichert}
              setFeld={(f) => { setBericht(null);
                setEntwurf((s2) => ({ ...s2, [r.id]: { ...(s2[r.id] ?? stand(r)), ...f } })); }}
              ereignisse={meta.paymentEvents}
              zahlentwurf={r.purchasing === null ? undefined
                : (zahlplan[r.id] ?? ausZahlplan(r.purchasing))}
              setZahlplan={(e) => { setBericht(null);
                setZahlplan((s2) => ({ ...s2, [r.id]: e })); }}
              bewertung={l === undefined ? null : gesamtbewertung(l.rating)}
              onBearbeiten={mayWrite && onEditingChange ? () => onEditingChange(true) : undefined}
              onOpenSupplier={onOpenSupplier}
              onChanged={replaceRelation} onRemoved={reload} onError={setError} />
          );
        });
      })()}

      {/*
        * SPEICHERN UND ABBRECHEN IM KONTEXT -- keine Leiste ueber der Seite.
        *
        * Bis hierher stand oben eine breite gruene Bearbeitungsleiste, und die
        * ganze Akte darunter baute sich um. Jetzt oeffnet „Bearbeiten" nur die
        * Felder der Karte; die Aktionen stehen direkt darunter. Dieselbe
        * Speicherlogik, derselbe Teilerfolgsbericht.
        */}
      {bearbeitet && (
        <div className="log__modus ek__modus" role="status">
          <span className="log__modushint">{t("ek.editHint")}</span>
          <span className="log__modusactions">
            {dirty && (
              <button className="btn btn--primary btn--small log__save" disabled={speichert}
                onClick={() => void speichern()}>
                {speichert ? t("props.saving") : t("log.save")}
              </button>
            )}
            <button className="btn btn--small log__cancel" disabled={speichert}
              onClick={() => { verwerfen(); onEditingChange?.(false); }}>
              {dirty ? t("common.cancel") : t("tk.done")}
            </button>
          </span>
        </div>
      )}

      {mayWrite && data !== null && (
        adding
          ? <RelationForm articleId={articleId} meta={meta}
              onDone={(r) => { setAdding(false); if (r) reload(); }} onError={setError} />
          : <button className="btn log__add" onClick={() => setAdding(true)}>{t("log.addRelation")}</button>
      )}
    </>
  );
}

/**
 * LOGISTIK & VERPACKUNG -- nicht vertraulich.
 *
 * Zolltarifnummer und Verpackungsstaende. Der Lieferant kommt hier NICHT vor:
 * wer wissen will, wie der Karton gebaut ist, muss nicht wissen, wer ihn
 * baut. Bei mehreren Beziehungen erscheinen mehrere Staende -- unterschieden
 * werden sie durch ihre Revision, nicht durch ihren Urheber.
 */
/**
 * Die Produktmasse fuer die Logistikansicht -- aus den Merkmalswerten.
 *
 * WARUM NICHT AUS `packaging_versions`
 *
 * Dort gibt es `product_length_mm` und Geschwister. Die bedeuten aber etwas
 * anderes: „das Mass, fuer das dieser Verpackungsstand ausgelegt wurde".
 * Steht es dort, ist es eine Aussage des Lieferanten ueber SEINE Annahme --
 * kein Produktmerkmal. Im Bestand ist keine dieser Spalten belegt.
 *
 * Das gueltige Mass des Artikels steht bei den Merkmalen, und dort bleibt es.
 * Diese Funktion liest es und zeigt es an. Sie schreibt nichts, sie legt
 * nichts an, sie spiegelt nichts -- eine Ansicht, keine zweite Datenhaltung.
 */
export interface Produktmasse {
  readonly laenge: string | null;
  readonly durchmesser: string | null;
  readonly breite: string | null;
  readonly hoehe: string | null;
  readonly gewicht: string | null;
}

function useProduktmasse(articleId: number): Produktmasse | null {
  const [masse, setMasse] = useState<Produktmasse | null>(null);
  useEffect(() => {
    let aktuell = true;
    void (async () => {
      try {
        const r = await api.articleProperties(articleId);
        if (!aktuell) return;
        /* Eine einzelne Zahl -- kein Bereich. Ein Mass mit Spanne waere
           entweder ein Fehler in der Quelle oder eine Toleranzangabe; beides
           gehoert nicht formatlos in eine Verpackungshierarchie. */
        /* Ein Mass, das am Produktmodell steht, gilt an der SKU genauso --
           dieselbe Regel wie in der Technik. Der eigene Wert geht vor. */
        const zahl = (code: string): string | null => {
          const v = r.items.find((i) => i.definition === code)
            ?? r.inherited?.items.find((i) => i.definition === code);
          return v ? v.num : null;
        };
        setMasse({
          laenge: zahl("length"), durchmesser: zahl("diameter"),
          breite: zahl("width"), hoehe: zahl("height"), gewicht: zahl("weight"),
        });
      } catch {
        /* Die Verpackungshierarchie ist ohne die Produktzeile vollstaendig
           lesbar. Ein Fehler beim Nachschlagen darf sie nicht verdecken. */
        if (aktuell) setMasse(null);
      }
    })();
    return () => { aktuell = false; };
  }, [articleId]);
  return masse;
}

/**
 * Die Steuerung, die der Rahmen braucht -- dieselbe wie in der Technik.
 *
 * Reiterwechsel, Artikelwechsel und Schliessen passieren AUSSERHALB dieser
 * Ansicht. Damit dort niemand ungespeicherte Eingaben verliert, meldet sie
 * dieselben drei Dinge nach oben wie `TechnikSteuerung`: ob etwas offen ist,
 * wie man es speichert, wie man es verwirft.
 *
 * Zwei gleich geformte Schnittstellen und kein gemeinsamer Typ -- das ist
 * Absicht, solange es zwei sind. Der gemeinsame Typ entsteht beim dritten
 * Bereich, dann mit einem Namen, der keinen Bereich nennt.
 */
export interface LogistikSteuerung {
  readonly dirty: boolean;
  /** Speichert alles Offene. `true`, wenn danach nichts mehr offen ist. */
  readonly save: () => Promise<boolean>;
  readonly discard: () => void;
}

/* ── Der Entwurf einer Verpackungsebene ───────────────────────────────────
 *
 * ZEICHENKETTEN, KEINE ZAHLEN -- dieselbe Regel wie in der Technik. Was
 * jemand tippt, ist Text, auch „4,5" und auch "". Erst beim Speichern wird
 * daraus eine Zahl oder ein Fehler; eine vorzeitige Umwandlung verschluckt
 * genau die Eingaben, ueber die zu reden ist.
 */
interface Ebenenfeld {
  readonly laenge: string;
  readonly breite: string;
  readonly hoehe: string;
  readonly gewicht: string;
  readonly anzahl: string;
}

const ausEbene = (l: PackagingLevel): Ebenenfeld => ({
  laenge: l.lengthMm ?? "", breite: l.widthMm ?? "", hoehe: l.heightMm ?? "",
  gewicht: l.grossWeightKg ?? "", anzahl: String(l.unitCount),
});

const ebeneGleich = (a: Ebenenfeld, b: Ebenenfeld): boolean =>
  a.laenge === b.laenge && a.breite === b.breite && a.hoehe === b.hoehe
  && a.gewicht === b.gewicht && a.anzahl === b.anzahl;

/** Die Materialgewichte einer Ebene als Entwurf: Materialcode -> Gramm. */
type Materialfeld = Record<string, string>;

const ausMaterial = (l: PackagingLevel): Materialfeld =>
  Object.fromEntries(l.materials.map((m) => [m.material, m.weightG]));

const materialGleich = (a: Materialfeld, b: Materialfeld): boolean => {
  const k = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  return k.every((x) => (a[x] ?? "").trim() === (b[x] ?? "").trim());
};

/**
 * DER FESTE LOGISTIKRAHMEN.
 *
 * WARUM ER UEBERHAUPT ENTSTEHT
 *
 * Die Logistikansicht zeigte bisher, was ERFASST ist: gibt es keinen
 * Verpackungsstand, steht dort ein Satz und viel leere Flaeche, und der
 * auffaelligste Knopf heisst „Neue Verpackung erfassen". Damit beantwortet
 * die Seite die Frage „was ist technisch angelegt?" statt „wie ist dieses
 * Produkt verpackt?".
 *
 * Das Raster dreht das um. Fuenf Karten stehen IMMER: Produkt, Innenkarton,
 * Masterkarton, VerpackungsG, Zoll. Welche Stufen es gibt, sagt das
 * Fachmodell -- nicht der Umstand, ob gerade ein Datensatz existiert. Wer die
 * Seite oeffnet, sieht dieselbe Ordnung wie beim letzten Mal und weiss, wo er
 * hinsehen muss.
 *
 * „n.a." UND „NICHT BESTIMMT" SIND ZWEI VERSCHIEDENE AUSSAGEN
 *
 *   n.a.            diese Verpackungsstufe gibt es bei diesem Produkt nicht
 *   nicht bestimmt  es gibt sie, aber der Wert ist noch nicht bekannt
 *
 * Ein leeres Feld sagt keines von beiden und wird deshalb nicht verwendet.
 *
 * Woran die Unterscheidung gemessen wird, solange es dafuer kein eigenes
 * Feld gibt: ein erfasster Verpackungsstand ZAEHLT SEINE STUFEN AUF. Fehlt
 * darin der Innenkarton, ist das die Aussage „es gibt keinen" -- jemand hat
 * diesen Stand aufgenommen und ihn nicht genannt. Ist ueberhaupt kein Stand
 * erfasst, ist nichts ausgesagt, und alles steht auf „nicht bestimmt".
 *
 * Das ist eine Ableitung, keine gespeicherte Wahrheit. Sie steht hier und
 * nicht verstreut in den Karten, damit sie an EINER Stelle zu aendern ist,
 * wenn das Fachmodell die Aussage eines Tages ausdruecklich traegt.
 */
export function LogistikRaster({ masse, staende, modellStaende, geladen, tariffCode, tariffErbe,
                          dutyRate, i18n, amModell = false }: {
  /** Auf der Seite des Produktmodells selbst -- dort ist der Stand kein geerbter. */
  amModell?: boolean;
  masse: Produktmasse | null;
  staende: readonly SupplierArticle[];
  /** Die Verpackungsstaende des Produktmodells -- sie gelten, wenn die SKU keinen eigenen hat. */
  modellStaende: ArticleLogisticsResponse["modelPackaging"];
  geladen: boolean;
  tariffCode: string | null;
  /** Das Modell, von dem die Tarifnummer kommt -- `null`, wenn sie der SKU gehoert. */
  tariffErbe: ModelRef | null;
  dutyRate: { ratePercent: string; validFrom: string } | null;
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  /* Der aktuelle Stand, gleich von welcher Beziehung er kommt: die Verpackung
     beschreibt das Produkt, nicht den Lieferweg. Gibt es mehrere, gilt der
     erste aktuelle -- mehr als einen aktuellen Stand kann es fachlich nicht
     geben. */
  const eigenerStand = staende.flatMap((r) => r.packagingVersions).find((v) => v.isCurrent) ?? null;
  /*
   * OHNE EIGENEN STAND GILT DER DES MODELLS -- UND DAS STEHT DABEI.
   *
   * Dieselbe Regel wie bei Texten und Merkmalen: der naehere Stand ersetzt
   * den ferneren, und ersetzt wird nie still. Die Karten tragen dann
   * „geerbt von HB200-U" im Kopf -- einmal je Karte, nicht an jeder Zahl.
   */
  const modellStand = eigenerStand === null
    ? modellStaende?.versions.find((v) => v.isCurrent) ?? null : null;
  const stand = eigenerStand ?? modellStand;
  const standErbe = modellStand !== null && !amModell
    ? t("log.inheritedFrom", { model: modellStaende!.model.code }) : null;
  const ebene = (typ: string): PackagingLevel | null =>
    stand?.levels.find((l) => l.type === typ) ?? null;

  /**
   * Ein Feld mit der richtigen Leeraussage.
   *
   * `stufeGibtEs` ist `false`, wenn ein Stand erfasst ist und diese Stufe
   * darin nicht vorkommt -- dann ist „n.a." die Auskunft und nicht
   * „unbekannt".
   */
  const feld = (wert: string | null | undefined, stufeGibtEs: boolean): React.ReactNode => {
    if (wert !== null && wert !== undefined && String(wert).trim() !== "") return wert;
    return <span className="log__leer">{stufeGibtEs ? t("log.undetermined") : t("log.notApplicable")}</span>;
  };
  /* Zahlen in der Schreibweise des Lesers -- „5,9 kg", nicht „5.9 kg". Die
     Technik zeigt denselben Wert, und zwei Schreibweisen fuer dieselbe Zahl
     auf zwei Reitern lesen sich wie zwei Zahlen. */
  const mm = (v: string | null | undefined): string | null =>
    v === null || v === undefined ? null : `${betrag(v, i18n.lang)} mm`;
  const kg = (v: string | null | undefined): string | null =>
    v === null || v === undefined ? null : `${betrag(v, i18n.lang)} kg`;
  const volumen = (v: string | null | undefined): string | null =>
    v === null || v === undefined ? null
      : Number(v).toLocaleString(i18n.lang, { minimumFractionDigits: 3, maximumFractionDigits: 6 });

  /*
   * GERECHNETES UND GEMESSENES IM SELBEN RASTER -- aber unterscheidbar.
   *
   * Maße, Stückzahl und Bruttogewicht sind gewogen und gemessen; CBM,
   * Verpackungsgewicht, Restgewicht und Materialanteil je Produkt entstehen
   * daraus. Wer beides gleich darstellt, laedt dazu ein, eine gerechnete Zahl
   * zu „korrigieren" -- und korrigiert damit nichts, weil sie beim naechsten
   * Aufbau wieder dasteht. Die gerechneten Zeilen tragen deshalb ihr eigenes
   * Wort (`log.computed`) und nie ein Eingabefeld.
   */
  const bilanzen = new Map(
    verpackungsbilanz(stand?.levels ?? [], masse?.gewicht ?? null)
      .map((b) => [b.ebene.type, b]));
  const gramm = (n: number): string =>
    `${n.toLocaleString(i18n.lang, { maximumFractionDigits: 2 })} ${t("log.gramShort")}`;

  const karton = (typ: "inner" | "master"): React.JSX.Element => {
    const l = ebene(typ);
    /* Kein Stand erfasst -> nichts ausgesagt. Stand erfasst, Stufe fehlt ->
       es gibt sie nicht. */
    const gibtEs = stand === null ? true : l !== null;
    const b = bilanzen.get(typ) ?? null;
    return (
      <RasterKarte titel={i18n.tCode("packLevel", typ, typ)} herkunft={standErbe}>
        <RasterZeile label={t("log.lengthOrDiameter")}>{feld(mm(l?.lengthMm), gibtEs)}</RasterZeile>
        <RasterZeile label={t("log.widthMm")}>{feld(mm(l?.widthMm), gibtEs)}</RasterZeile>
        <RasterZeile label={t("log.heightShort")}>{feld(mm(l?.heightMm), gibtEs)}</RasterZeile>
        <RasterZeile label={t("log.unitCount")}>{feld(l ? String(l.unitCount) : null, gibtEs)}</RasterZeile>
        <RasterZeile label={t("log.grossWeight")}>
          {feld(kg(l?.grossWeightKg), gibtEs)}
        </RasterZeile>
        {/* Das Volumen je Stueck ist eine Division und kommt mit zwanzig
            Nachkommastellen aus der Datenbank. Gezeigt werden so viele, wie
            eine Frachtrechnung braucht -- der genaue Wert bleibt in den
            Daten, die Zeile bleibt lesbar. */}
        <RasterZeile label={t("log.cbm")}>{feld(volumen(l?.volumeM3), gibtEs)}</RasterZeile>
        <RasterZeile label={t("log.cbmPerUnit")}>{feld(volumen(l?.cbmPerUnit), gibtEs)}</RasterZeile>
        {/*
          * DAS VERPACKUNGSGEWICHT WIRD NICHT EINGETRAGEN.
          *
          * Am Masterkarton des TP154 sind es 800 g: 19,10 kg minus sechs
          * Innenkartons zu 3,05 kg. Die Zeile nennt die Herleitung mit, weil
          * dieselbe Stelle vorher 2,60 kg zeigte -- eine Zahl, die plausibel
          * aussah und sechsmal die Innenverpackung doppelt zaehlte.
          */}
        <RasterZeile label={t("log.packagingWeight")}>
          {b === null ? feld(null, gibtEs) : (
            <>{gramm(b.verpackungG)}
              <span className="log__rasternote">
                {" · "}{t("log.computed")}{" · "}
                {b.inhaltQuelle === "produkt"
                  ? t("log.balanceContent", { n: b.inhaltStueck })
                  : t("log.balanceContentLevel", { n: b.inhaltStueck,
                      level: i18n.tCode("packLevel", b.inhaltQuelle, b.inhaltQuelle) })}
              </span></>
          )}
        </RasterZeile>
        {/*
          * ── DIE DIFFERENZ TRAEGT KEINE MATERIALART (MICRO 001) ────
          *
          * HIER STAND EINE KOPPLUNG AN `residual_material_id`, und sie
          * hatte zwei Folgen, die beide falsch waren:
          *
          *   1. `restG === 0 && residualMaterial === null` war die
          *      Bedingung fuer „nichts uebrig". Trug die Ebene eine
          *      ALTE Zuordnung, fiel sie trotz Differenz 0 in den
          *      anderen Zweig -- und zeigte „0 g · Pappe".
          *   2. Im anderen Zweig wurde der Materialname an den Betrag
          *      gehaengt: „800 g · Pappe".
          *
          * Der Befund der Abnahme: Arne hat den Innenkarton des TP154
          * fachlich korrigiert (267 g Pappe + 33 g Kunststoff = 300 g,
          * Differenz 0) -- und die Zeile stand weiter da, mit einer
          * Materialart an einer Null.
          *
          * JETZT: bei Differenz 0 gibt es die Zeile NICHT. Es gibt
          * nichts, was nicht zugeordnet waere, und eine Zeile mit
          * „0 g" ist eine Auskunft ueber nichts. Bei einer Differenz
          * steht der BETRAG, und nur er.
          *
          * DIE ALTE ZUORDNUNG BLEIBT LESBAR -- aber nicht hier. Sie
          * steht als Historienhinweis in der Gewichtsbilanz („frueher
          * zugeordnet: Pappe") und wird dort weder gerechnet noch an
          * die aktuelle Differenz gehaengt.
          *
          * Ein negatives Verpackungsgewicht ist kein Rest, sondern ein
          * Widerspruch in den Messwerten; es wird weiter als solcher
          * gemeldet.
          */}
        {b !== null && b.restG !== 0 && (
          <RasterZeile label={t("log.residualWeight")}>
            <>{gramm(b.restG)}
              {b.restG < 0 && (
                <span className="log__rasternote">
                  {" · "}{t("log.balanceImpossible")}
                </span>
              )}</>
          </RasterZeile>
        )}
      </RasterKarte>
    );
  };

  /*
   * VerpackungsG: der Materialeinsatz JE VERKAUFTEM PRODUKT.
   *
   * Hier stand die rohe Materialmasse der Ebene -- 266 g Pappe, 33 g
   * Kunststoff, ohne Umlage und ohne Summe. Das ist nicht die Groesse, nach
   * der die Abgabe berechnet wird: die 133,33 g Pappe des Masterkartons
   * fehlten in dieser Karte vollstaendig, weil sie kein erfasster Messwert
   * sind, sondern der zugeordnete Rest. Am TP154 lauten die beiden Zahlen
   * 399,33 g Pappe und 33,00 g Kunststoff; die Herleitung je Stufe steht
   * daneben, damit sie nachvollziehbar bleibt.
   */
  const materialien = materialJeProdukt(stand?.levels ?? [], masse?.gewicht ?? null);

  return (
    <div className="log__raster">
      <RasterKarte titel={t("log.levelProduct")}>
        <RasterZeile label={t("log.lengthOrDiameter")}>
          {feld(masse?.durchmesser ? `Ø ${mm(masse.durchmesser)}` : mm(masse?.laenge), true)}
        </RasterZeile>
        {/* Bei einem runden Produkt gibt es keine Breite -- das ist keine
            Wissensluecke, sondern die Form. */}
        <RasterZeile label={t("log.widthMm")}>
          {masse?.durchmesser ? <span className="log__leer">{t("log.notApplicable")}</span>
            : feld(mm(masse?.breite), true)}
        </RasterZeile>
        <RasterZeile label={t("log.heightShort")}>{feld(mm(masse?.hoehe), true)}</RasterZeile>
        {/* Das Produktgewicht ist ein MERKMAL und wird in der Technik
            gepflegt. Hier steht derselbe Wert, nicht ein zweiter. */}
        <RasterZeile label={t("log.productWeight")}>
          {feld(kg(masse?.gewicht), true)}
        </RasterZeile>
      </RasterKarte>

      {karton("inner")}
      {karton("master")}

      <RasterKarte titel={t("log.groupPackagingAct")} herkunft={standErbe}>
        {materialien.length === 0
          ? <RasterZeile label={t("log.materialShare")}>
              <span className="log__leer">{geladen ? t("log.undetermined") : "…"}</span>
            </RasterZeile>
          : materialien.map((m) => (
              <RasterZeile key={m.material}
                label={i18n.tCode("packMaterial", m.material, m.material)}>
                {gramm(m.grammJeProdukt)}
                <span className="log__rasternote">
                  {" · "}{t("log.perProduct")}
                  {m.anteile.map((a) =>
                    ` · ${i18n.tCode("packLevel", a.stufe, a.stufe)} ${gramm(a.grammJeProdukt)}`)}
                </span>
              </RasterZeile>
            ))}
      </RasterKarte>

      <RasterKarte titel={t("log.groupCustoms")}
        herkunft={tariffErbe ? t("log.inheritedFrom", { model: tariffErbe.code }) : null}>
        <RasterZeile label={t("log.tariffCode")}>
          {tariffCode ? <code className="log__tariff">{tariffCode}</code>
            : <span className="log__leer">{t("log.undetermined")}</span>}
        </RasterZeile>
        <RasterZeile label={t("log.dutyRate")}>
          {/* Derselbe Formatierer wie in der grossen Karte. „2.700 %" statt
              „2,70 %" entsteht, wenn man die Zeichenkette roh hinschreibt --
              und ein Zollsatz, der um den Faktor tausend danebenliegt, faellt
              in einer Kalkulation erst ganz am Ende auf. */}
          {dutyRate
            ? <>{betrag(dutyRate.ratePercent, i18n.lang)} %{" "}
                <span className="log__rasternote">{t("log.since", { date: dutyRate.validFrom })}</span></>
            : <span className="log__leer">{t("log.undetermined")}</span>}
        </RasterZeile>
      </RasterKarte>
    </div>
  );
}

/** Eine Karte des festen Rasters -- dieselbe Form wie eine Technikgruppe. */
function RasterKarte({ titel, herkunft = null, children }: {
  titel: string;
  /** „geerbt von …" -- im Kopf, in derselben Form wie an den Textkarten. */
  herkunft?: string | null;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="log__rasterkarte" aria-label={titel}>
      <h4 className="log__grouptitle">
        {titel}
        {herkunft !== null && <span className="log__herkunft">{" · "}{herkunft}</span>}
      </h4>
      <div className="log__rasterzeilen">{children}</div>
    </section>
  );
}

/** Label links, Wert rechts -- dasselbe Raster wie in der Technik. */
function RasterZeile({ label, children }: {
  label: string; children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="log__rasterzeile">
      <span className="log__label">{label}</span>
      <span className="log__vals">{children}</span>
    </div>
  );
}

export function ArticlePackagingSection({ articleId, meta, mayWrite, mayConfirm, attention, i18nLabel,
                                          showTariff = true, editing = false, onEditingChange, steuerung }: {
  articleId: number;
  meta: MetaResponse;
  mayWrite: boolean;
  mayConfirm: boolean;
  attention: React.ReactNode;
  i18nLabel: string;
  /** Bearbeitungsmodus des BEREICHS -- er gehoert dem Rahmen, nicht hierher. */
  editing?: boolean;
  onEditingChange?: (v: boolean) => void;
  /** Wird mit der aktuellen Steuerung belegt -- siehe `LogistikSteuerung`. */
  steuerung?: React.MutableRefObject<LogistikSteuerung | null>;
  /**
   * Ob die Zolltarifnummer hier miterscheint.
   *
   * In der Arbeitsansicht ja -- dort ist „Logistik" der einzige Ort, an dem
   * sie gepflegt wird. In der Produktakte nein: dort folgt die Gliederung der
   * Preisliste, und Zoll ist ein EIGENER Abschnitt. Zweimal dasselbe Feld auf
   * einer durchgehenden Seite waeren zwei Wahrheiten.
   */
  showTariff?: boolean;
}): React.JSX.Element {
  const produktmasse = useProduktmasse(articleId);
  const i18n = useI18n();
  const t = i18n.t;
  const { data, error, setData, setError, replaceRelation, reload } = useLogistics(articleId);

  const staende = data?.supplierArticles ?? [];
  const ohneVerpackung = data !== null
    && staende.every((r) => r.packagingVersions.length === 0);

  /* ── Der Bearbeitungsmodus ──────────────────────────────────────────────
   *
   * DER BLEISTIFT VERAENDERT DEN MODUS, NICHT DEN ORT.
   *
   * Die Leseansicht bleibt, wo sie ist: dieselbe Tabelle, dieselben Spalten,
   * dieselbe Reihenfolge. Im Bearbeitungsmodus werden aus den Zahlen
   * Eingabefelder -- an derselben Stelle, in derselben Zelle. Eine zweite
   * Maske „Verpackung bearbeiten" waere ein zweiter Ort fuer dieselbe
   * Auskunft, und man muesste beim Tippen im Kopf behalten, wo die Zahl
   * spaeter steht.
   *
   * WAS NICHT EDITIERBAR WIRD: das Volumen und das Volumen je Stueck. Beide
   * werden aus den Aussenmassen gerechnet (`volume_m3` ist in der Datenbank
   * GENERATED). Ein Eingabefeld dafuer waere eine zweite Wahrheit, die beim
   * naechsten Massaenderung still falsch wird.
   */
  const [entwurf, setEntwurf] = useState<Record<number, Ebenenfeld>>({});
  const [material, setMaterial] = useState<Record<number, Materialfeld>>({});
  const [fehler, setFehler] = useState<Record<number, string>>({});
  const [speichert, setSpeichert] = useState(false);
  const [bericht, setBericht] = useState<{ ok: number; offen: number } | null>(null);

  /** Alle Ebenen des jeweils AKTUELLEN Standes -- nur die sind bearbeitbar. */
  const ebenen = staende.flatMap((r) =>
    (r.packagingVersions.find((v) => v.isCurrent)?.levels ?? []));

  const ebeneOffen = (l: PackagingLevel): boolean => {
    const e = entwurf[l.id];
    const m = material[l.id];
    return (e !== undefined && !ebeneGleich(e, ausEbene(l)))
        || (m !== undefined && !materialGleich(m, ausMaterial(l)));
  };
  const offeneEbenen = ebenen.filter(ebeneOffen);
  const dirty = offeneEbenen.length > 0;

  const verwerfen = useCallback((): void => {
    setEntwurf({}); setMaterial({}); setFehler({}); setBericht(null);
  }, []);

  useEffect(() => { if (!editing) { verwerfen(); } }, [editing, verwerfen]);
  useEffect(() => { verwerfen(); }, [articleId, verwerfen]);

  /*
   * SPEICHERN -- EBENE FUER EBENE, MIT AUSDRUECKLICHEM TEILERFOLG.
   *
   * Eine Verpackungsebene ist ein eigener Datensatz mit eigener Adresse.
   * Drei geaenderte Ebenen sind drei Aufrufe, und schlaegt der dritte fehl,
   * sind die ersten beiden trotzdem geschrieben. Diese Ansicht behauptet
   * dann NICHT, alles sei gesichert -- sie sagt, wie viele es waren, laesst
   * die uebrigen Eingaben stehen und benennt den Fehler an seiner Ebene.
   *
   * Masse und Materialien sind zwei Adressen (`PATCH /packaging-levels/{id}`
   * und `PUT .../materials`). Geschrieben wird nur, was sich geaendert hat:
   * wer nur ein Materialgewicht korrigiert, loest keine Massaenderung aus.
   */
  const speichern = useCallback(async (): Promise<boolean> => {
    const offen = ebenen.filter(ebeneOffen);
    if (offen.length === 0) return true;

    setSpeichert(true);
    setError(null);
    const neueFehler: Record<number, string> = {};
    let ok = 0;

    for (const l of offen) {
      const e = entwurf[l.id];
      const m = material[l.id];
      try {
        if (e !== undefined && !ebeneGleich(e, ausEbene(l))) {
          const zahl = (v: string): number | null =>
            v.trim() === "" ? null : Number(v.trim().replace(",", "."));
          const anzahl = Number(e.anzahl.trim());
          if (!Number.isInteger(anzahl) || anzahl < 1) {
            neueFehler[l.id] = t("log.errUnitCount");
            continue;
          }
          /*
           * EIN LEERES FELD IST KEINE FALSCHE EINGABE.
           *
           * Bisher galt jedes leere Mass als Fehler -- ein falsch uebernommenes
           * Kartonmass liess sich damit nur durch ein anderes ersetzen, also
           * durch Raten, oder durch Loeschen der ganzen Ebene samt
           * Bruttogewicht und Materialanteilen, die stimmen. Leer heisst jetzt
           * NICHT BESTIMMT und wird als `null` geschrieben (Migration 0053).
           *
           * Eine 0 bleibt ein Fehler: sie behauptet einen Karton ohne
           * Ausdehnung, statt eine Luecke zu benennen.
           */
          for (const [wert, name] of [[e.laenge, "log.dimLength"], [e.breite, "log.dimWidth"],
                                       [e.hoehe, "log.dimHeight"]] as const) {
            if (wert.trim() === "") continue;
            const n = zahl(wert);
            if (n === null || !Number.isFinite(n) || n <= 0) {
              neueFehler[l.id] = t("log.errDimension", { feld: t(name) });
            }
          }
          const g = zahl(e.gewicht);
          if (e.gewicht.trim() !== "" && (g === null || !Number.isFinite(g) || g <= 0)) {
            neueFehler[l.id] = t("log.errWeight");
          }
          if (neueFehler[l.id] !== undefined) continue;
          await api.updatePackagingLevel(l.id, {
            lengthMm: zahl(e.laenge), widthMm: zahl(e.breite), heightMm: zahl(e.hoehe),
            grossWeightKg: g, unitCount: anzahl,
          });
        }
        if (m !== undefined && !materialGleich(m, ausMaterial(l))) {
          /*
           * NICHT VORHANDENES MATERIAL IST EINE FEHLENDE ZEILE, KEINE NULL.
           *
           * Ein leeres Feld heisst „dazu ist nichts erfasst" und nicht
           * „null Gramm". Genau so nimmt es die Adresse auch entgegen
           * (`PackagingMaterialInput`: Gewicht groesser null).
           */
          const liste = Object.entries(m)
            .filter(([, g2]) => g2.trim() !== "")
            .map(([code, g2]) => ({ material: code as PackagingMaterial,
                                    weightG: g2.trim().replace(",", ".") }));
          if (liste.some((x) => !(Number(x.weightG) > 0))) {
            neueFehler[l.id] = t("log.errMaterial");
            continue;
          }
          await api.setPackagingMaterials(l.id, { materials: liste });
        }
        ok++;
      } catch (e2) {
        neueFehler[l.id] = errorText(i18n, e2);
      }
    }

    setFehler(neueFehler);
    setBericht({ ok, offen: offen.length - ok });
    /* Die erfolgreich gespeicherten Entwuerfe fallen weg, die uebrigen
       bleiben stehen -- der Fehler steht an ihrer Zeile. */
    setEntwurf((s2) => Object.fromEntries(
      Object.entries(s2).filter(([id]) => neueFehler[Number(id)] !== undefined)));
    setMaterial((s2) => Object.fromEntries(
      Object.entries(s2).filter(([id]) => neueFehler[Number(id)] !== undefined)));
    setSpeichert(false);
    await reload();
    return offen.length - ok === 0;
  }, [ebenen, entwurf, material, i18n, t, reload, setError]);

  /* Der Rahmen fragt ueber diese Kennung nach offenen Eingaben. */
  const speichernRef = useRef(speichern);
  speichernRef.current = speichern;
  useEffect(() => {
    if (!steuerung) return;
    steuerung.current = { dirty, save: () => speichernRef.current(), discard: verwerfen };
    return () => { steuerung.current = null; };
  }, [dirty, verwerfen, steuerung]);

  const bearbeitet = editing && mayWrite;

  return (
    <>
      <h3 className="detail__section">{i18nLabel}</h3>
      {attention}
      {error !== null && <p className="log__error">{errorText(i18n, error)}</p>}

      {/*
        * DER MODUS IST BESCHRIFTET, NICHT NUR ANGEDEUTET -- dieselbe Regel
        * wie in der Technik. Und die Leiste zeigt nur, was zu tun ist:
        * solange nichts getippt ist, steht dort allein der Ausgang.
        */}
      {bearbeitet && (
        <div className="log__modus" role="status">
          <span className="log__moduslabel">{t("log.editing")}</span>
          <span className="log__modushint">{t("log.editHint")}</span>
          <span className="log__modusactions">
            {dirty && (
              <button className="btn btn--primary btn--small log__save" disabled={speichert}
                onClick={() => void speichern()}>
                {speichert ? t("props.saving") : t("log.save")}
              </button>
            )}
            <button className="btn btn--small log__cancel" disabled={speichert}
              onClick={() => { verwerfen(); onEditingChange?.(false); }}>
              {dirty ? t("common.cancel") : t("tk.done")}
            </button>
          </span>
        </div>
      )}

      {bericht !== null && (
        <p className={bericht.offen === 0 ? "log__bericht" : "log__bericht log__bericht--teil"}
           role="status">
          {bericht.offen === 0
            ? t("tk.savedAll", { n: bericht.ok })
            : bericht.ok === 0
              ? t("tk.savedNone")
              : t("tk.savedSome", { ok: bericht.ok, offen: bericht.offen })}
        </p>
      )}

      {/*
        * ZOLL ALS EIGENE BOX -- dieselbe Form wie die uebrigen Cluster.
        *
        * Sie steht ausserhalb der Verpackungsstaende, weil sie zum ARTIKEL
        * gehoert und nicht zu einer Supplier-Beziehung: dieselbe Ware hat
        * dieselbe Tarifnummer, gleich von wem sie kommt.
        */}
      {/*
        * IM LESEMODUS DECKT DAS RASTER DEN ZOLL AB.
        *
        * Die grosse Karte bleibt fuer die Pflege: dort steht der Schreibweg
        * fuer Tarifnummer und Satz. Beide gleichzeitig zu zeigen hiesse
        * dieselbe Auskunft zweimal auf einer Seite -- und beim naechsten Mal
        * fragt sich jemand, welche der beiden gilt.
        */}
      {showTariff && bearbeitet && (
        <section className="log__group log__group--zoll">
          <h4 className="log__grouptitle">{t("log.groupCustoms")}</h4>
          <TariffCode articleId={articleId} value={data?.tariffCode ?? null}
            inheritedFrom={data?.tariffInheritedFrom ?? null}
            dutyRate={data?.dutyRate ?? null}
            mayWrite={mayWrite} onChanged={setData} onError={setError} />
          {/* Ursprungsland und Nettogewicht stehen neben der Tarifnummer,
              weil sie dieselbe Frage beantworten: was muss auf das
              Zollpapier? Getrennt davon stuenden sie an drei Stellen. */}
          <Zollstammdaten articleId={articleId} mayWrite={mayWrite} onError={setError} />
        </section>
      )}

      {/*
        * ZUERST DAS RASTER, DANN DIE STAENDE.
        *
        * Das Raster beantwortet die Frage, mit der jemand herkommt: wie ist
        * dieses Produkt verpackt? Die Verpackungsstaende mit Revision,
        * Pruefungen und Gewichtsbilanz beantworten eine andere -- wie ist der
        * Stand entstanden und was hat sich geaendert. Beides bleibt; nur die
        * Reihenfolge folgt jetzt der Frage und nicht der Datenstruktur.
        */}
      {!bearbeitet && (
        <LogistikRaster masse={produktmasse} staende={staende}
          modellStaende={data?.modelPackaging ?? null} geladen={data !== null}
          tariffCode={data?.tariffCode ?? null} tariffErbe={data?.tariffInheritedFrom ?? null}
          dutyRate={data?.dutyRate ?? null} i18n={i18n} />
      )}

      {data === null && error === null && <p className="log__note">{t("common.loading")}</p>}
      {ohneVerpackung && bearbeitet && (
        <p className="log__note">
          {data?.modelPackaging?.versions.some((v) => v.isCurrent)
            ? t("log.packagingInheritHint", { model: data.modelPackaging.model.code })
            : t("log.noPackaging")}
        </p>
      )}

      {/*
        * DIE STAENDE STEHEN HINTER EINER KLAPPE -- IM LESEMODUS.
        *
        * Revision, Pruefungen, Gewichtsbilanz und der Weg „Neue Verpackung
        * erfassen" beantworten, wie ein Stand ENTSTANDEN ist. Wer die Akte
        * oeffnet, fragt zuerst, wie das Produkt verpackt IST -- das
        * beantwortet das Raster darueber. Nichts ist entfernt: eine Klappe
        * weiter steht alles, was vorher hier stand, und im
        * Bearbeitungsmodus steht es ohnehin offen da.
        */}
      <details className="log__staende" open={bearbeitet}>
        <summary className="log__staendesum">{t("log.packagingDetails")}</summary>
      {staende.map((r) => (
        <PackagingForRelation key={r.id} relation={r} meta={meta} produktmasse={produktmasse}
          mayWrite={mayWrite} mayConfirm={mayConfirm}
          bearbeitet={bearbeitet} materialien={meta.packagingMaterials.map((x) => x.code)}
          entwurf={entwurf} material={material}
          fehler={fehler} speichert={speichert}
          setEbene={(id, f) => { setBericht(null);
            setEntwurf((s2) => ({ ...s2, [id]: { ...(s2[id] ?? ausEbene(
              ebenen.find((l) => l.id === id)!)), ...f } }));
            setFehler((s2) => { const n = { ...s2 }; delete n[id]; return n; }); }}
          setMaterial={(id, code, wert) => { setBericht(null);
            setMaterial((s2) => ({ ...s2, [id]: { ...(s2[id] ?? ausMaterial(
              ebenen.find((l) => l.id === id)!)), [code]: wert } }));
            setFehler((s2) => { const n = { ...s2 }; delete n[id]; return n; }); }}
          onChanged={replaceRelation} onError={setError} />
      ))}
      </details>
    </>
  );
}

/**
 * Die Zolltarifnummer.
 *
 * Sie steht hier, weil sie eine Angabe der SKU ist -- nicht der
 * Supplier-Beziehung und nicht des Verpackungsstands. Der Wert wird ERFASST;
 * es gibt keinen Vorschlag, keine Ableitung und keine Klassifizierung.
 */
export function TariffCode({ articleId, value, inheritedFrom = null, dutyRate, mayWrite,
                            onChanged, onError }: {
  articleId: number;
  value: string | null;
  /**
   * Das Produktmodell, dessen Tarifnummer hier gilt -- `null`, wenn sie der
   * SKU selbst gehoert.
   *
   * Beim Bearbeiten startet das Feld dann LEER, mit der Modellnummer als
   * Platzhalter. Sonst stuende sie im Feld, und ein blosses „Speichern"
   * kopierte sie an die SKU -- die SKU haette ab dann eine eigene Nummer,
   * die bei der naechsten Aenderung am Modell stehen bliebe.
   */
  inheritedFrom?: ModelRef | null;
  /*
   * Der Zollsatz DIESER Tarifnummer -- nur dort, wo er nicht schon anderswo
   * auf derselben Seite steht.
   *
   * Die Produktakte fuehrt ihn als eigene Zeile im Zollabschnitt und uebergibt
   * ihn deshalb NICHT. Der Arbeitsbereich Logistik hat keinen solchen
   * Abschnitt: dort stand bis hierher die Tarifnummer allein, obwohl der
   * Server den Satz an derselben Adresse mitliefert. Eine Nummer ohne ihren
   * Satz beantwortet die halbe Frage.
   *
   * `null` heisst "keiner hinterlegt" -- ausdruecklich nicht "zollfrei".
   */
  dutyRate?: { readonly ratePercent: string; readonly validFrom: string } | null;
  mayWrite: boolean;
  onChanged: (d: ArticleLogisticsResponse) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  async function save(): Promise<void> {
    try {
      onChanged(await api.setTariffCode(articleId, { tariffCode: draft.trim() === "" ? null : draft.trim() }));
      setOpen(false);
    } catch (e) { onError(e); }
  }

  return (
    <>
      <div className="detail__field">
        <div className="detail__label">{t("log.tariffCode")}</div>
        <div className="detail__value">
          {/* Ein Code, keine Zahl. Fuehrende Nullen bleiben stehen. */}
          {value ? <code className="log__tariff">{value}</code> : <Unknown />}
          {inheritedFrom !== null && (
            <span className="log__herkunft">{" · "}{t("log.inheritedFrom", { model: inheritedFrom.code })}</span>
          )}
          {mayWrite && (
            <button className="btn log__inline"
              onClick={() => { setDraft(inheritedFrom !== null ? "" : value ?? ""); setOpen(!open); }}>
              {t("common.edit")}
            </button>
          )}
        </div>
      </div>
      {dutyRate !== undefined && (
        <div className="detail__field">
          <div className="detail__label">{t("log.dutyRate")}</div>
          <div className="detail__value">
            {dutyRate === null ? <Unknown /> : (
              <>
                {betrag(dutyRate.ratePercent, i18n.lang)} %{" "}
                <span className="log__since">{t("log.since", { date: dutyRate.validFrom })}</span>
              </>
            )}
          </div>
        </div>
      )}
      {open && mayWrite && (
        <div className="log__form">
          <input className="list__search" value={draft} onChange={(e) => setDraft(e.target.value)}
            aria-label={t("log.tariffCode")}
            placeholder={inheritedFrom !== null && value ? value : t("log.tariffPlaceholder")} />
          <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
          <button className="btn" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
          <p className="log__note">{t("log.tariffHint")}</p>
          {inheritedFrom !== null && (
            <p className="log__note">{t("log.tariffInheritHint", { model: inheritedFrom.code })}</p>
          )}
        </div>
      )}
    </>
  );
}

/* ── Zollstammdaten (0080) ────────────────────────────────────────────────
 *
 * ZWEI EBENEN UND ZWEI FAKTEN, ALLE VIER SICHTBAR GETRENNT.
 *
 * Oben die Vorgaben am Artikel -- Ursprungsland und Nettogewicht je in
 * einer eigenen Zeile mit eigener Herkunft und eigener Unterschrift.
 * Darunter je Lieferbeziehung, was GILT und woher es kommt.
 *
 * Die Trennung ist der ganze Zweck dieser Ansicht. Wer nur das Ergebnis
 * saehe, wuesste nicht, ob er den Artikel oder die Beziehung aendern muss
 * -- und wer die beiden Fakten gemeinsam saehe, hielte eine bestaetigte
 * Herkunft fuer ein bestaetigtes Gewicht.
 *
 * WAS HIER NICHT STEHT: ein Vorschlag aus dem Kalkulationsgewicht. Er ist
 * fachlich vorgesehen, aber fuer diesen Block nicht freigegeben -- und ein
 * Vorschlag, den man mit einem Klick uebernimmt, ist die kuerzeste Strecke
 * von einer Kalkulationsgroesse zu einer Zollangabe.
 */

const ZOLLFAKTEN = [
  { code: "origin_country", label: "cust.originCountry", einheit: "" },
  /*
   * „Nettogewicht fuer Zoll" und nicht „Nettogewicht · Zollangabe am
   * Artikel" (§10). Der Zusatz sagte, in welcher TABELLE der Wert
   * steht; die Ueberschrift soll sagen, was er bedeutet. Wo er
   * herkommt, steht als eigene, leise Zeile darunter.
   */
  { code: "net_weight_kg", label: "cust.netWeightCustoms", einheit: " kg" },
] as const satisfies readonly { code: CustomsFactCode; label: string; einheit: string }[];

const faktWert = (f: CustomsFact | null): string =>
  f === null ? "" : (f.factCode === "origin_country" ? f.originCountry : f.netWeightKg);

/** Ein aufgeloester Wert samt der Ebene, von der er kommt -- oder „fehlt". */
function Zollwert({ r, einheit, i18n }: {
  r: ResolvedCustomsValue; einheit: string; i18n: I18n;
}): React.JSX.Element {
  if (r.value === null) {
    return <span className="log__leer" data-testid="cust-missing">{i18n.t("cust.missing")}</span>;
  }
  return (
    <>
      <strong>{r.value}{einheit}</strong>
      <span className="log__herkunft">
        {" · "}
        {i18n.t(r.from === "supplier_article" ? "cust.fromRelation"
          : r.from === "product" ? "cust.fromProductNet" : "cust.fromArticle")}
      </span>
    </>
  );
}

/** Der Stand EINES Fakts: Herkunft, Methode, Belastbarkeit, Unterschrift. */
function Zollstand({ f, i18n }: { f: CustomsFact; i18n: I18n }): React.JSX.Element {
  const gueltig = f.quality === "belastbar" && f.confirmedAt !== null;
  return (
    <span className="log__herkunft">
      {i18n.tCode("textOrigin", f.origin)}
      {" · "}{i18n.tCode("propMethod", f.method)}
      {" · "}{i18n.tCode("quality", f.quality)}
      {" · "}
      <span data-testid="cust-gueltig">
        {i18n.t(gueltig ? "cust.customsTruth" : "cust.notCustomsTruth")}
      </span>
    </span>
  );
}

/** Die Maske fuer EINEN Fakt auf EINER Ebene. */
function Zollmaske({ fact, code, speichern, entfernen, abbrechen, i18n }: {
  fact: CustomsFact | null;
  code: CustomsFactCode;
  speichern: (e: Record<string, unknown>) => Promise<void>;
  entfernen: (() => Promise<void>) | null;
  abbrechen: () => void;
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const istLand = code === "origin_country";
  const [wert, setWert] = useState(faktWert(fact));
  const [origin, setOrigin] = useState<string>(fact?.origin ?? "supplier");
  const [method, setMethod] = useState<string>(fact?.method ?? "erfasst");
  const [quality, setQuality] = useState<string>(fact?.quality ?? "pruefung_erforderlich");
  const [confirmed, setConfirmed] = useState(fact !== null && fact.confirmedAt !== null);
  const [note, setNote] = useState(fact?.note ?? "");

  return (
    <div className="log__form" data-testid={`cust-form-${code}`}>
      <input className="list__search log__small" value={wert}
        maxLength={istLand ? 2 : undefined}
        onChange={(e) => setWert(istLand ? e.target.value.toUpperCase() : e.target.value)}
        aria-label={t(istLand ? "cust.originCountry" : "cust.netWeight")}
        placeholder={t(istLand ? "cust.originCountryPlaceholder" : "cust.netWeightPlaceholder")} />
      <select className="list__select" value={origin} aria-label={t("log.origin")}
        onChange={(e) => setOrigin(e.target.value)}>
        {LOGISTICS_ORIGINS.map((o) => <option key={o} value={o}>{i18n.tCode("textOrigin", o, o)}</option>)}
      </select>
      <select className="list__select" value={method} aria-label={t("log.method")}
        onChange={(e) => setMethod(e.target.value)}>
        {LOGISTICS_METHODS.map((m) => <option key={m} value={m}>{i18n.tCode("propMethod", m, m)}</option>)}
      </select>
      <select className="list__select" value={quality} aria-label={t("cust.quality")}
        onChange={(e) => setQuality(e.target.value)}>
        {QUALITY_STATUS_CODES.map((q) => <option key={q} value={q}>{i18n.tCode("quality", q, q)}</option>)}
      </select>
      <label className="log__check">
        <input type="checkbox" checked={confirmed} data-testid={`cust-confirm-${code}`}
          onChange={(e) => setConfirmed(e.target.checked)} />
        {t("cust.confirm")}
      </label>
      <input className="list__search" value={note} onChange={(e) => setNote(e.target.value)}
        aria-label={t("cust.note")} placeholder={t("cust.notePlaceholder")} />
      <button className="btn btn--primary" data-testid={`cust-save-${code}`}
        onClick={() => void speichern({
          ...(istLand ? { originCountry: wert.trim() } : { netWeightKg: wert.trim() }),
          origin, method, quality, confirmed,
          note: note.trim() === "" ? null : note.trim(),
        })}>{t("common.save")}</button>
      <button className="btn" onClick={abbrechen}>{t("common.cancel")}</button>
      {entfernen !== null && (
        <button className="btn" data-testid={`cust-remove-${code}`}
          onClick={() => void entfernen()}>{t("common.remove")}</button>
      )}
      <p className="log__note">{t("cust.hint")}</p>
    </div>
  );
}

export function Zollstammdaten({ articleId, mayWrite, onError }: {
  articleId: number; mayWrite: boolean; onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [data, setData] = useState<ArticleCustomsResponse | null>(null);
  /* Offen ist hoechstens eine Maske: `0` ist der Artikel, sonst die
     Beziehung -- zusammen mit dem Fakt, um den es geht. */
  const [offen, setOffen] = useState<{ ebene: number; code: CustomsFactCode } | null>(null);
  const istOffen = (ebene: number, code: CustomsFactCode): boolean =>
    offen !== null && offen.ebene === ebene && offen.code === code;
  const umschalten = (ebene: number, code: CustomsFactCode): void =>
    setOffen(istOffen(ebene, code) ? null : { ebene, code });

  useEffect(() => {
    let aktuell = true;
    api.articleCustoms(articleId)
      .then((d) => { if (aktuell) setData(d); })
      .catch((e) => { if (aktuell) onError(e); });
    return () => { aktuell = false; };
  }, [articleId, onError]);

  const nachSchreiben = (d: ArticleCustomsResponse): void => { setData(d); setOffen(null); };
  const satz = (s: CustomsFactSet, c: CustomsFactCode): CustomsFact | null =>
    c === "origin_country" ? s.originCountry : s.netWeightKg;

  return (
    <div className="log__zoll" data-testid="cust-block">
      {ZOLLFAKTEN.map(({ code, label, einheit }) => {
        const f = data === null ? null : satz(data.articleFacts, code);
        /* Nur das Nettogewicht hat einen kanonischen Produktwert (§7). */
        const uebernommen = code === "net_weight_kg"
          ? (data?.productNetWeightKg ?? null) : null;
        return (
          <div key={code}>
            <div className="detail__field">
              <div className="detail__label">{t(label)}</div>
              <div className="detail__value" data-testid={`cust-article-${code}`}>
                {/*
                  * ── DREI LAGEN, UND KEINE DAVON HEISST „fehlt",
                  *    SOLANGE DIE ZAHL IM HAUS IST (§7/§10) ────────
                  *
                  * 1. Ein erfasster Zollfakt -- er gilt und traegt
                  *    seine Herkunft, Methode und Unterschrift.
                  * 2. Kein Fakt, aber ein Produkt-Nettogewicht: es
                  *    GILT, und zwar als uebernommener Wert. Vorher
                  *    stand hier „fehlt", waehrend das Gewicht in
                  *    den Merkmalen gepflegt war -- und daneben eine
                  *    Aufforderung, dieselbe Zahl ein zweites Mal
                  *    einzutragen.
                  * 3. Weder noch -- dann fehlt es wirklich.
                  *
                  * Das Ursprungsland hat kein Pendant in den
                  * Produktmerkmalen und kennt deshalb nur 1 und 3.
                  */}
                {f !== null ? (<>
                  <strong>{faktWert(f)}{einheit}</strong>{" "}
                  {/* „Artikelwert" sagt, auf welcher EBENE die Angabe
                      steht; der Stand daneben sagt, wie belastbar sie
                      ist. Zwei verschiedene Fragen, zwei Zeilen. */}
                  <span className="log__herkunft">{t("cust.articleDefault")}</span>{" "}
                  <Zollstand f={f} i18n={i18n} />
                </>) : uebernommen !== null ? (<>
                  <strong data-testid="cust-uebernommen">{uebernommen}{einheit}</strong>{" "}
                  <span className="log__herkunft">{t("cust.fromProductNet")}</span>
                </>) : <Unknown />}
                {mayWrite && (
                  <button className="btn log__inline" data-testid={`cust-edit-article-${code}`}
                    onClick={() => umschalten(0, code)}>
                    {f !== null ? t("common.edit")
                      : uebernommen !== null ? t("cust.recordExplicit")
                      : t("cust.record")}
                  </button>
                )}
              </div>
            </div>
            {istOffen(0, code) && mayWrite && (
              <Zollmaske fact={f} code={code} i18n={i18n} abbrechen={() => setOffen(null)}
                entfernen={f === null ? null : async () => {
                  try { nachSchreiben(await api.removeArticleCustomsFact(articleId, code)); }
                  catch (e) { onError(e); }
                }}
                speichern={async (e) => {
                  try { nachSchreiben(await api.setArticleCustomsFact(articleId, code, e as never)); }
                  catch (err) { onError(err); }
                }} />
            )}
          </div>
        );
      })}

      {(data?.supplierArticles ?? []).map((b) => (
        <div key={b.supplierArticleId} className="log__zollbeziehung">
          {/*
            * ── EINE LIEFERANTENANGABE, KEINE ZWEITE PRODUKTWAHRHEIT
            *    (§8/§10) ──────────────────────────────────────────
            *
            * Hier stand nur „TRIECO" -- und darunter ein
            * Nettogewicht, das genauso aussah wie das am Artikel.
            * Zwei Zahlen fuer dasselbe physische Gewicht, ohne dass
            * eine von beiden sich als die nachrangige auswies.
            *
            * Fachlich (§6) ist der Lieferantenwert QUELLE: er wird
            * geliefert, im QC geprueft, gegebenenfalls nachgemessen
            * -- und dann gilt der bestaetigte Produktwert. Die
            * Ueberschrift sagt das jetzt.
            */}
          <div className="detail__label">
            {t("cust.supplierStated", { name: b.supplierName })}
          </div>
          {ZOLLFAKTEN.map(({ code, label, einheit }) => {
            const f = satz(b.facts, code);
            const r = code === "origin_country" ? b.resolved.originCountry : b.resolved.netWeightKg;
            return (
              <div key={code}>
                <div className="detail__field">
                  <div className="detail__label">{t(label)}</div>
                  <div className="detail__value" data-testid={`cust-rel-${b.supplierArticleId}-${code}`}>
                    <Zollwert r={r} einheit={einheit} i18n={i18n} />
                    {f !== null && <> <Zollstand f={f} i18n={i18n} /></>}
                    {mayWrite && (
                      <button className="btn log__inline"
                        data-testid={`cust-edit-${b.supplierArticleId}-${code}`}
                        onClick={() => umschalten(b.supplierArticleId, code)}>
                        {/*
                          * KEINE ABWEICHUNG IST NICHT DASSELBE WIE
                          * FEHLT (§25/§26).
                          *
                          * Hier stand immer „Abweichung erfassen",
                          * sobald die Lieferbeziehung keinen eigenen
                          * Fakt trug. Das ist richtig, SOLANGE der
                          * Artikel einen hat: dann weicht man von
                          * ihm ab.
                          *
                          * Traegt auch der Artikel keinen, steht
                          * daneben „fehlt" -- und die Schaltflaeche
                          * lud dazu ein, von nichts abzuweichen. Wer
                          * sie druckte, erfasste in Wahrheit die
                          * ERSTE Angabe, und zwar an der Beziehung
                          * statt am Artikel, wo sie fuer alle
                          * Beziehungen gelten wuerde.
                          *
                          * Drei Faelle, drei Worte: eigener Fakt ->
                          * bearbeiten; Artikelwert vorhanden ->
                          * Abweichung erfassen; nichts vorhanden ->
                          * erfassen.
                          */}
                        {f !== null
                          ? t("common.edit")
                          : r.value === null
                            ? t("cust.record")
                            : t("cust.addOverride")}
                      </button>
                    )}
                  </div>
                </div>
                {istOffen(b.supplierArticleId, code) && mayWrite && (
                  <Zollmaske fact={f} code={code} i18n={i18n} abbrechen={() => setOffen(null)}
                    entfernen={f === null ? null : async () => {
                      try { nachSchreiben(await api.removeRelationCustomsFact(b.supplierArticleId, code)); }
                      catch (e) { onError(e); }
                    }}
                    speichern={async (e) => {
                      try {
                        nachSchreiben(await api.setRelationCustomsFact(
                          b.supplierArticleId, code, e as never));
                      } catch (err) { onError(err); }
                    }} />
                )}
              </div>
            );
          })}
        </div>
      ))}
      {data !== null && data.supplierArticles.length === 0 && (
        <p className="log__note">{t("log.noRelations")}</p>
      )}
    </div>
  );
}

/** Eine Supplier-Produkt-Beziehung samt ihrer Verpackungsgeschichte. */
/**
 * Eine Beschaffungsbeziehung -- die Einkaufsseite.
 *
 * Kein Verpackungsstand: der gehoert in Logistik & Verpackung. Was hier
 * steht, ist vertraulich und verlaesst diesen Bereich nicht.
 */
/** Der Entwurf einer Beschaffungsbeziehung. Zeichenketten, wie ueberall. */
interface Beziehungsfeld {
  readonly nummer: string;
  readonly moq: string;
  readonly vielfaches: string;
  readonly lieferzeit: string;
}

function PurchasingRelation({ relation, mayWrite, mayWritePrice, mayConfirm, bearbeitet, entwurf,
                              speichert, setFeld, ereignisse, zahlentwurf, setZahlplan,
                              bewertung, onBearbeiten, onOpenSupplier,
                              onChanged, onRemoved, onError }: {
  relation: SupplierArticle;
  mayWrite: boolean;
  /** `price.input.write` -- der Einkaufspreis hat sein eigenes Recht. */
  mayWritePrice: boolean;
  mayConfirm: boolean;
  bearbeitet: boolean;
  entwurf: Beziehungsfeld | undefined;
  speichert: boolean;
  setFeld: (f: Partial<Beziehungsfeld>) => void;
  /* Der Ereigniskatalog kommt aus `meta` -- diese Datei fuehrt ihn nicht. */
  ereignisse: MetaResponse["paymentEvents"];
  zahlentwurf: Zahlentwurf | undefined;
  setZahlplan: (e: Zahlentwurf) => void;
  /*
   * Die Bewertung gehoert dem LIEFERANTEN und nicht dieser Beziehung.
   *
   * Sie steht trotzdem in diesem Raster, weil sie dorthin gehoert, wo man
   * den Partner beurteilt. Damit sie bei zwei Beziehungen desselben
   * Suppliers nicht zweimal erscheint, entscheidet der Aufrufer, welche
   * Beziehung sie zeigt -- diese Komponente stellt nur den Platz.
   */
  /** Die verdichtete Lieferantenbewertung (Durchschnitt), oder null. */
  bewertung: number | null;
  /** Den Bearbeitungsmodus aus der Karte heraus oeffnen -- fehlt ohne Recht. */
  onBearbeiten?: (() => void) | undefined;
  /** Ins Lieferantenportal, zu genau diesem Lieferanten. */
  onOpenSupplier?: ((supplierId: number, fokus?: "bewertung") => void) | undefined;
  onChanged: (r: SupplierArticle) => void;
  onRemoved: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element | null {
  const i18n = useI18n();
  const t = i18n.t;

  async function confirmQuality(): Promise<void> {
    try { onChanged(await api.updateSupplierArticle(relation.id, { quality: "belastbar" })); }
    catch (e) { onError(e); }
  }
  async function remove(): Promise<void> {
    try { await api.deleteSupplierArticle(relation.id); onRemoved(); } catch (e) { onError(e); }
  }
  /* Bestaetigt wird die Ebene, auf der der gezeigte Plan vereinbart ist --
     nicht die, auf der man ihn gerade bearbeiten wuerde. */
  async function bestaetigePlan(ebene: PaymentTermsLevel): Promise<void> {
    try {
      await api.confirmPaymentTerms(ebene === "supplier" ? "suppliers" : "supplier-articles",
        ebene === "supplier" ? p!.supplier.id : relation.id);
      onRemoved();
    } catch (e) { onError(e); }
  }

  /*
   * Ohne `purchasing.read` liefert der Server hier `null`. Der Bereich
   * Einkauf ist fuer einen solchen Nutzer ohnehin nicht erreichbar -- diese
   * Abfrage ist die zweite Sperre, falls er es doch einmal waere.
   */
  const p = relation.purchasing;
  if (p === null) return null;
  const e = entwurf ?? {
    nummer: p.supplierArticleNumber ?? "",
    moq: p.moq === null ? "" : String(p.moq),
    vielfaches: p.orderMultiple === null ? "" : String(p.orderMultiple),
    lieferzeit: p.leadTimeDays === null ? "" : String(p.leadTimeDays),
  };

  /*
   * VIER BOXEN, NACH ARBEITSWERT GEORDNET.
   *
   * Hier stand eine einzige Liste: Supplier, Nummer, MOQ,
   * Bestellvielfaches, Lieferzeit und der Einkaufspreis, alle gleich laut,
   * darunter die Bewertung ueber die volle Breite. Wer taeglich beschafft,
   * sucht darin zuerst den Preis -- und findet ihn als sechste Zeile
   * zwischen Stammdaten.
   *
   * Die Reihenfolge folgt jetzt dem, was am haeufigsten gebraucht wird:
   * Produkt und Pricing oben, Partner und Bewertung darunter. Die
   * Bewertung bleibt fachlich unveraendert und wird nur leiser -- sie ist
   * eine Einschaetzung des Partners und keine Angabe, die man beim
   * Bestellen ablesen muss.
   */
  const preis = p.currentPrice;
  /* Die Vorrangregel steht an EINER Stelle -- hier wird sie nur benutzt. */
  const plan = massgeblich(p);
  /*
   * NUR, WAS GEPFLEGT IST -- im Lesemodus.
   *
   * MOQ, Bestellvielfaches und Lieferzeit stehen als eine ruhige Zeile unter
   * dem Preis, und nur, wenn es sie gibt. Vier „nicht bestimmt" untereinander
   * waren eine Verwaltungsflaeche ohne Auskunft. Im Bearbeitungsmodus stehen
   * alle vier als Felder da -- dort wird genau das gefuellt.
   */
  const nebenangaben = [
    p.supplierArticleNumber !== null ? `${t("ek.supplierArtNo")} ${p.supplierArticleNumber}` : null,
    p.moq !== null ? `${t("log.moq")} ${p.moq}` : null,
    p.orderMultiple !== null ? `${t("log.orderMultiple")} ${p.orderMultiple}` : null,
    p.leadTimeDays !== null ? `${t("log.leadTime")} ${t("log.days", { n: p.leadTimeDays })}` : null,
  ].filter((x): x is string => x !== null);

  return (
    /*
     * LIEFERANT & EINKAUF -- ERGEBNIS ZUERST.
     *
     * Die Karte beantwortet drei Fragen: von wem, zu welchem Preis, zu
     * welchen Konditionen. Die Lieferantenbewertung steht als EINE Zahl im
     * Kopf („AGC ★ 4,1") -- gepflegt wird sie im Lieferantenportal, wohin
     * „Lieferant öffnen" fuehrt. Partnerblock und Bewertungskriterien stehen
     * hier nicht mehr; sie gehoeren dem Lieferanten, nicht dem Artikel.
     *
     * Der Einkaufspreis steht EINMAL, an der Stelle, an der man ihn sucht,
     * und wird dort auch erfasst (`PUT /supplier-articles/{id}/prices`).
     */
    <section className={bearbeitet ? "log__relation ek__karte ek__karte--edit" : "log__relation ek__karte"}>
      <header className="log__relationhead ek__kopf">
        <span className="ek__rolle">{t("ek.supplier")}</span>
        {/*
          * NAME UND NOTE SIND DER WEG INS PORTAL (UX-Nachtrag 002).
          *
          * „AGC ★ 4,0" ist die Stelle, auf die man schaut -- und deshalb die
          * Stelle, auf die man klickt. Sie fuehrt zur Akte des Lieferanten,
          * dort in die Konditionen und bis an die Bewertung. Ohne Recht auf
          * das Portal bleibt sie Text.
          *
          * Der Lieferantenname ist ein Quelldatum und bleibt in jeder Sprache
          * unveraendert.
          */}
        {(() => {
          const inhalt = (<>
            <h4 className="log__supplier">{p.supplier.name}</h4>
            <span className="ek__score" title={t("ek.ratingTitle")}>
              {bewertung === null
                ? <span className="ek__scoreleer">{t("bew.notRated")}</span>
                : <>★ {bewertung.toLocaleString(i18n.lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</>}
            </span>
          </>);
          return onOpenSupplier
            ? <button type="button" className="ek__link ek__lieferantlink"
                title={t("ek.openSupplierRating", { name: p.supplier.name })}
                aria-label={t("ek.openSupplierRating", { name: p.supplier.name })}
                onClick={() => onOpenSupplier(p.supplier.id, "bewertung")}>{inhalt}</button>
            : inhalt;
        })()}
        {/* Ein offener Pruefstand ist eine Handlung wert -- ein bestaetigter
            ist keine Auskunft fuer den Alltag und steht deshalb nicht da. */}
        {relation.quality !== "belastbar" && (
          <span className={`log__quality log__quality--${relation.quality}`}>
            {i18n.tCode("quality", relation.quality)}
          </span>
        )}
        <span className="ek__kopfaktionen">
          {onBearbeiten && !bearbeitet && (
            <button type="button" className="btn btn--small ek__bearbeiten" onClick={onBearbeiten}>
              {t("common.edit")}
            </button>
          )}
        </span>
      </header>

      {bearbeitet && (
        <p className="ek__herkunft">
          {i18n.tCode("textOrigin", relation.origin)} · {i18n.tCode("propMethod", relation.method)}
          {mayWrite && mayConfirm && relation.quality !== "belastbar" && (
            <button className="btn log__inline" onClick={confirmQuality}>{t("log.confirm")}</button>
          )}
          {mayWrite && relation.packagingVersions.length === 0 && (
            <button className="btn log__inline" onClick={remove}>{t("log.removeRelation")}</button>
          )}
        </p>
      )}

      <div className="log__cluster ek__cluster">
        {/* ── Preis und Zahlung ─────────────────────────────────────── */}
        <section className="log__group ek__spalte ek__preis" aria-label={t("ek.groupPricing")}>
          <div className="ek__kennzahl">
            <span className="ek__kennlabel">{t("ek.price")}</span>
            {preis === null ? <Unknown /> : (
              <span className="ek__preiswert">
                <strong className="ek__betrag">{betrag(preis.amount, i18n.lang)}</strong>
                {" "}<span className="ek__waehrung">{preis.currency}</span>
              </span>
            )}
            {/* Die Preisbasis ist heute ausnahmslos EXW -- `sp_type_chk` laesst
                nichts anderes zu. Sie steht trotzdem da: ein Preis ohne
                Lieferbedingung ist keine Kondition. */}
            {preis !== null && (
              <span className="ek__preisbasis">
                {t("ek.incoterm", { term: t("ek.exw") })} · {t("ek.validSince", { date: preis.validFrom })}
              </span>
            )}
            {mayWritePrice && (
              <EinkaufspreisErfassen relationId={relation.id} i18n={i18n}
                onGespeichert={onRemoved} onError={onError} />
            )}
          </div>
          <div className="ek__kennzahl">
            <span className="ek__kennlabel">{t("ek.paymentTerms")}</span>
            {plan === null ? <Unknown />
              : <span className="ek__plan">{zahlplanSatz(plan, i18n)}</span>}
            {/* Ob der Plan dem Lieferanten oder nur diesem Artikel gilt, ist
                Teil der Kondition -- eine kurze Zeile, kein Metadatum. */}
            {plan !== null && (
              <p className="ek__planherkunft">
                <span className={`log__quality log__quality--${plan.quality}`}>
                  {i18n.tCode("quality", plan.quality)}
                </span>
                {" "}
                {t(plan.level === "article" ? "ek.termsFromArticle" : "ek.termsFromSupplier")}
                {mayWrite && mayConfirm && plan.quality !== "belastbar" && (
                  <button type="button" className="btn log__inline ek__planconfirm"
                    onClick={() => void bestaetigePlan(plan.level)}>{t("log.confirm")}</button>
                )}
              </p>
            )}
            {bearbeitet && zahlentwurf !== undefined && (
              <Zahlplaneditor entwurf={zahlentwurf} ereignisse={ereignisse}
                hatStandard={p.supplier.paymentTerms !== null}
                speichert={speichert} i18n={i18n} setEntwurf={setZahlplan} />
            )}
          </div>
        </section>

        {/* ── Artikel beim Lieferanten ──────────────────────────────── */}
        {bearbeitet ? (
          <section className="log__group ek__spalte ek__produkt" aria-label={t("ek.groupProduct")}>
            <div className="log__rows">
              <LogZeile label={t("log.supplierArticleNumber")}>
                <input className="log__input log__input--text" value={e.nummer} disabled={speichert}
                  aria-label={t("log.supplierArticleNumber")}
                  onChange={(ev) => setFeld({ nummer: ev.target.value })} />
              </LogZeile>
              <LogZeile label={t("log.moq")}>
                <input className="log__input" inputMode="numeric" value={e.moq} disabled={speichert}
                  aria-label={t("log.moq")}
                  onChange={(ev) => setFeld({ moq: ev.target.value })} />
              </LogZeile>
              <LogZeile label={t("log.orderMultiple")}>
                <input className="log__input" inputMode="numeric" value={e.vielfaches} disabled={speichert}
                  aria-label={t("log.orderMultiple")}
                  onChange={(ev) => setFeld({ vielfaches: ev.target.value })} />
              </LogZeile>
              <LogZeile label={t("log.leadTime")}>
                <input className="log__input" inputMode="numeric" value={e.lieferzeit} disabled={speichert}
                  aria-label={t("log.leadTime")}
                  onChange={(ev) => setFeld({ lieferzeit: ev.target.value })} />
              </LogZeile>
            </div>
          </section>
        ) : nebenangaben.length > 0 && (
          <p className="ek__neben ek__produkt">{nebenangaben.join(" · ")}</p>
        )}
      </div>

      {relation.note && <p className="log__note">{relation.note}</p>}
    </section>
  );
}

/**
 * Einen neuen Einkaufspreis erfassen -- im Kontext des Preises.
 *
 * Derselbe Weg wie zuvor im eigenen Block „Einkaufspreis" darunter
 * (`PUT /supplier-articles/{id}/prices`, Recht `price.input.write`). Ein
 * neuer Preis ueberschreibt nichts: er gilt ab seinem Datum, der bisherige
 * bleibt als Vorgaenger in der Datenbank.
 */
function EinkaufspreisErfassen({ relationId, i18n, onGespeichert, onError }: {
  relationId: number; i18n: I18n; onGespeichert: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [wert, setWert] = useState("");
  const [waehrung, setWaehrung] = useState("USD");
  const [ab, setAb] = useState("");
  const [busy, setBusy] = useState(false);
  /* Gegen den zweiten Klick eines Doppelklicks -- vor dem naechsten Render. */
  const laeuft = useRef(false);
  /*
   * GUELTIG-AB IM FORMULARSTATE (Gueltig-ab-Formularstate 001): Oeffnen setzt
   * den heutigen Kalendertag wirklich -- Safari zeigte ein leeres Datumsfeld
   * mit heutigem Datum, und „Speichern" blieb grau. Ein gesperrter Knopf sagt
   * jetzt, warum.
   */
  const betrag = Number(wert.replace(",", "."));
  const grund: string | null =
      wert.trim() === "" || !(betrag > 0) ? t("form.why.amountMissing")
    : ab === "" ? t("form.why.dateMissing")
    : null;
  async function speichern(): Promise<void> {
    if (laeuft.current || grund !== null) return;
    laeuft.current = true;
    setBusy(true);
    try {
      await api.setSupplierPrice(relationId, {
        amount: Number(wert.replace(",", ".")), currency: waehrung, validFrom: ab,
      });
      setOffen(false); setWert(""); setAb("");
      onGespeichert();
    } catch (e) { onError(e); } finally { setBusy(false); laeuft.current = false; }
  }
  if (!offen) {
    return (
      <button type="button" className="btn btn--small ek__preiserfassen"
        onClick={() => { setOffen(true); setWert(""); setAb(heuteLokal()); }}>{t("akte.setPrice")}</button>
    );
  }
  return (
    <div className="ek__preisform">
      <label>{t("akte.amount")}
        <input className="input" inputMode="decimal" value={wert} onChange={(e) => setWert(e.target.value)} />
      </label>
      <label>{t("akte.currency")}
        {/* ISO-Codes, in jeder Sprache gleich -- aus der Deklaration. */}
        <select className="input" value={waehrung} onChange={(e) => setWaehrung(e.target.value)}>
          {PRICE_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label>{t("akte.validFrom")}
        <input className="input" type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
      </label>
      <span className="ek__preisformaktionen">
        <button className="btn btn--primary btn--small ek__preisspeichern" disabled={busy || grund !== null}
          onClick={() => void speichern()}>{t("common.save")}</button>
        <button className="btn btn--small ek__preisabbrechen" onClick={() => { setOffen(false); setWert(""); setAb(""); }}>
          {t("common.cancel")}</button>
      </span>
      {grund !== null && <span className="akte__sperrgrund" role="status">{grund}</span>}
    </div>
  );
}

/**
 * Die Verpackungsstaende einer Beziehung -- die Logistikseite.
 *
 * OHNE den Lieferantennamen. Er waere hier eine vertrauliche Angabe in einem
 * Bereich, der es nicht ist.
 */
/** Was eine Ebene zum Bearbeiten braucht -- einmal beschrieben, dreimal gereicht. */
interface Bearbeitung {
  bearbeitet: boolean;
  /*
   * DER WERTEVORRAT DER MATERIALIEN -- aus `meta`, nicht aus dieser Datei.
   *
   * Auswahlkatalog und Artikelwert sind zwei Dinge: HOMEBASE kennt zentral
   * `cardboard`, `plastic`, `wood`, `other`; am Artikel steht nur, was
   * tatsaechlich gewogen wurde. Eine Liste im Quelltext waere ein dritter
   * Ort fuer denselben Katalog.
   */
  materialien: readonly string[];
  entwurf: Record<number, Ebenenfeld>;
  material: Record<number, Materialfeld>;
  fehler: Record<number, string>;
  speichert: boolean;
  setEbene: (id: number, f: Partial<Ebenenfeld>) => void;
  setMaterial: (id: number, code: string, wert: string) => void;
}

function PackagingForRelation({ relation, meta, produktmasse, mayWrite, mayConfirm,
                                onChanged, onError, ...bearb }: {
  relation: SupplierArticle;
  meta: MetaResponse;
  produktmasse: Produktmasse | null;
  mayWrite: boolean;
  mayConfirm: boolean;
  onChanged: (r: SupplierArticle) => void;
  onError: (e: unknown) => void;
} & Bearbeitung): React.JSX.Element | null {
  const i18n = useI18n();
  const t = i18n.t;
  const [addingVersion, setAddingVersion] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const versions = relation.packagingVersions;
  const current = versions.find((v) => v.isCurrent) ?? null;
  const history = versions.filter((v) => !v.isCurrent);

  if (versions.length === 0 && !mayWrite) return null;

  return (
    <section className="log__relation">
      {current !== null && (
        <Version version={current} meta={meta} produktmasse={produktmasse} mayWrite={mayWrite} mayConfirm={mayConfirm}
          onChanged={onChanged} onError={onError} {...bearb} />
      )}

      {history.length > 0 && (
        <div className="log__history">
          <button className="log__historytoggle" onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? t("log.hideHistory") : t("log.showHistory", { n: history.length })}
          </button>
          {/* Historische Staende bleiben vollstaendig erhalten und lesbar.
              Eine neue Revision hat keinen davon ersetzt. */}
          {/*
            * HISTORISCHE STAENDE BLEIBEN LESEANSICHT -- auch im
            * Bearbeitungsmodus. Eine frühere Revision ist Geschichte; sie
            * zu aendern hiesse, sie zu faelschen. Geaendert wird der
            * aktuelle Stand, und wenn sich der Verpackungsstand wirklich
            * geaendert hat, entsteht eine neue Revision.
            */}
          {showHistory && history.map((v) => (
            <Version key={v.id} version={v} meta={meta} produktmasse={produktmasse} mayWrite={mayWrite} mayConfirm={mayConfirm}
              onChanged={onChanged} onError={onError}
              {...bearb} bearbeitet={false} />
          ))}
        </div>
      )}

      {mayWrite && (
        addingVersion
          ? <VersionForm erste={relation.currentRevision === null} meta={meta}
              anlegen={(input) => api.createPackagingVersion(relation.id, input)}
              onDone={(r) => { setAddingVersion(false); if (r) onChanged(r); }} onError={onError} />
          : <button className="btn log__add" onClick={() => setAddingVersion(true)}>
              {t("log.addVersion")}
            </button>
      )}
    </section>
  );
}

/** Ein Verpackungsstand mit seinen Ebenen, Materialien und Nachweisen. */
function Version({ version, meta, produktmasse, mayWrite, mayConfirm, onChanged, onError, ...bearb }: {
  version: PackagingVersion;
  meta: MetaResponse;
  produktmasse: Produktmasse | null;
  mayWrite: boolean;
  mayConfirm: boolean;
  onChanged: (r: SupplierArticle) => void;
  onError: (e: unknown) => void;
} & Bearbeitung): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [addingTest, setAddingTest] = useState(false);

  /*
   * Die Annahme des Verpackungsstandes gegen das gepflegte Merkmal.
   *
   * Nur wenn beide da sind UND sich unterscheiden. Eine Uebereinstimmung ist
   * keine Nachricht, und eine fehlende Annahme erst recht nicht.
   */
  const vergleich = (annahme: string | null, merkmal: string | null): boolean =>
    annahme !== null && merkmal !== null && Number(annahme) !== Number(merkmal);
  const abweichendesMass =
    produktmasse !== null && (
      vergleich(version.productLengthMm, produktmasse.laenge) ||
      vergleich(version.productWidthMm, produktmasse.breite) ||
      vergleich(version.productHeightMm, produktmasse.hoehe))
      ? `${version.productLengthMm ?? "?"} × ${version.productWidthMm ?? "?"} × ${version.productHeightMm ?? "?"} mm`
      : null;

  async function removeVersion(): Promise<void> {
    try { onChanged(await api.deletePackagingVersion(version.id)); } catch (e) { onError(e); }
  }
  async function confirmQuality(): Promise<void> {
    try { onChanged(await api.updatePackagingVersion(version.id, { quality: "belastbar" })); }
    catch (e) { onError(e); }
  }

  return (
    <article className={`log__version${version.isCurrent ? " log__version--current" : ""}`}>
      <header className="log__versionhead">
        <span className="log__revision">{t("log.revision", { n: version.revision })}</span>
        {version.isCurrent
          ? <span className="log__badge log__badge--current">{t("log.current")}</span>
          : <span className="log__badge">{t("log.historic")}</span>}
        <span className="log__reason">{i18n.tCode("packReason", version.changeReason)}</span>
        {version.provider && (
          <span className="log__provider">{i18n.tCode("packProvider", version.provider)}</span>
        )}
        <span className={`log__quality log__quality--${version.quality}`}>
          {i18n.tCode("quality", version.quality)}
        </span>
        {/*
          * ── KEIN ABSCHLUSS MIT OFFENER DIFFERENZ (§4) ─────────────
          *
          * Gesperrt wird mit `offeneDifferenzen` aus `shared` -- genau
          * der Funktion, mit der auch der Fachkern die Bestaetigung
          * abweist. Zwei Rechnungen waeren zwei Regeln, und eine von
          * beiden liesse irgendwann durch, was die andere verbietet.
          *
          * SICHTBAR GESPERRT, NICHT VERSTECKT. Die Schaltflaeche
          * bleibt stehen und sagt im `title`, warum sie nicht geht --
          * eine verschwundene Schaltflaeche laesst den Benutzer
          * suchen, eine gesperrte sagt ihm, was zu tun ist. Die
          * Differenz selbst steht in der Gewichtsbilanz darunter.
          *
          * `produktmasse` kann fehlen -- dann gibt es keine Bilanz und
          * nichts zu sperren; `offeneDifferenzen` ist in diesem Fall
          * leer, nicht voll.
          */}
        {mayWrite && mayConfirm && version.quality !== "belastbar" && (() => {
          const offen = offeneDifferenzen(version.levels, produktmasse?.gewicht ?? null);
          return (
            <button className="btn log__inline" onClick={confirmQuality}
              data-testid="log-standbestaetigen"
              disabled={offen.length > 0}
              title={offen.length > 0 ? t("log.confirmBlockedBalance") : undefined}>
              {t("log.confirm")}
            </button>
          );
        })()}
        {mayWrite && version.isCurrent && (
          <button className="btn log__inline" onClick={removeVersion}>{t("log.removeVersion")}</button>
        )}
      </header>

      {version.changeNote && <p className="log__note">{version.changeNote}</p>}
      {/*
        * DER QUELLVERMERK DES STANDES.
        *
        * `note` und `changeNote` sind zwei verschiedene Saetze: der eine sagt,
        * WOHER dieser Stand kommt, der andere, WARUM er geaendert wurde.
        * Gezeigt wurde bisher nur der zweite -- und der ist am Referenz-
        * artikel leer, waehrend der erste „Nachholimport aus der Masterliste"
        * traegt. Die Herkunft stand also da und war nicht zu sehen.
        *
        * Die Beziehung zeigt ihren Vermerk schon laenger auf demselben Weg
        * (`relation.note`); hier fehlte er.
        */}
      {version.note && <p className="log__note">{version.note}</p>}

      <dl className="log__facts">
        <Fact label={t("log.validFrom")}>{version.validFrom ?? <Unknown />}</Fact>
        {/*
          * DIE PRODUKTMASSE STEHEN JETZT IN DER HIERARCHIE, NICHT HIER OBEN.
          *
          * Hier standen `version.productLengthMm` und Geschwister -- das Mass,
          * fuer das dieser Stand ausgelegt wurde. Im gesamten Bestand ist
          * keine dieser 232 Zeilen belegt, die Angabe war also immer leer.
          *
          * Sie bleibt als Abweichungshinweis erhalten: weicht die Annahme des
          * Verpackungsstandes vom gepflegten Merkmal ab, ist das eine echte
          * Meldung. Stimmen beide ueberein oder fehlt die Annahme, gibt es
          * nichts zu sagen -- und dann steht hier auch nichts.
          */}
        {abweichendesMass !== null && (
          <Fact label={t("log.productDimsDeviation")}>{abweichendesMass}</Fact>
        )}
        <Fact label={t("log.provenance")}>
          {i18n.tCode("textOrigin", version.origin)} · {i18n.tCode("propMethod", version.method)}
        </Fact>
      </dl>

      {version.levels.length === 0
        ? <p className="log__note">{t("log.noLevels")}</p>
        : (
          /*
           * FUENF BOXEN STATT EINER TABELLE.
           *
           * Hier stand eine Tabelle mit sieben Zahlenspalten. Sie war gut
           * darin, drei Ebenen zu VERGLEICHEN -- und schlecht darin, EINE zu
           * pflegen: ein Eingabefeld in einer Zahlenspalte ist ein Feld in
           * einer Spalte, und wer die Hoehe des Innenkartons sucht, zaehlt
           * Spalten.
           *
           * Jetzt dieselbe Sprache wie in der Technik: eine Box je Cluster,
           * eine Zeile je Angabe, Beschriftung links, Wert rechts. Der
           * Bleistift macht aus dem Wert ein Feld -- an derselben Stelle,
           * in derselben Zeile, in derselben Box.
           *
           * DIE REIHENFOLGE IST DIE PHYSISCHE: Produkt, dann was es umgibt.
           * Die Datenbank kennt nur `inner` und `master`; das Produkt ist
           * keine Verpackungsebene, sondern ihr Inhalt. Fuer das Verstaendnis
           * gehoert es trotzdem an den Anfang.
           */
          <div className="log__cluster">
            <ProduktBox masse={produktmasse} i18n={i18n}
              stand={{ laenge: version.productLengthMm,
                       breite: version.productWidthMm,
                       hoehe: version.productHeightMm }} />
            {[...version.levels]
              .sort((a, b) => rang(a.type) - rang(b.type))
              .map((l) => (<EbenenBox key={l.id} level={l} levels={version.levels}
                produktgewicht={produktmasse?.gewicht ?? null} {...bearb} />))}
            <VerpackungsBox levels={version.levels} aktuell={version.isCurrent}
              produktgewicht={produktmasse?.gewicht ?? null} meta={meta}
              mayConfirm={mayWrite && mayConfirm} i18n={i18n}
              onChanged={onChanged} onError={onError} {...bearb} />
          </div>
        )}

      {version.tests.length > 0 && (
        <ul className="log__tests">
          {version.tests.map((x) => (
            <li key={x.id}>
              <span className={`log__result log__result--${x.result}`}>
                {i18n.tCode("packResult", x.result)}
              </span>
              {" "}
              {i18n.tCode("packTestType", x.testType)}
              {x.performedOn && <span className="log__testdate">{x.performedOn}</span>}
              {x.standard && <span className="log__teststandard">{x.standard}</span>}
              {/* Eine fachliche Referenz, kein Dateipfad und kein Anhang. */}
              {x.reference && <span className="log__testref">{x.reference}</span>}
              {x.note && <span className="log__testnote">{x.note}</span>}
              {mayWrite && (
                <button className="btn log__inline"
                  onClick={async () => {
                    try { onChanged(await api.deletePackagingTest(x.id)); } catch (e) { onError(e); }
                  }}>{t("common.remove")}</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayWrite && (
        addingTest
          ? <TestForm versionId={version.id} meta={meta}
              onDone={(r) => { setAddingTest(false); if (r) onChanged(r); }} onError={onError} />
          : <button className="btn log__add" onClick={() => setAddingTest(true)}>{t("log.addTest")}</button>
      )}
    </article>
  );
}

/**
 * MATERIAL JE VERKAUFTEM ARTIKEL -- die Groesse, die Vera braucht.
 *
 * `PRODUCT_DATA_MODEL.md` §19.1 nennt als fachlichen Mindestbedarf von Vera V1
 * genau drei Angaben: Produktgewicht, Pappe je Produkt, Kunststoff je Produkt.
 * §19.2 nennt auch den Weg dorthin -- das Materialgewicht einer vollstaendigen
 * Verpackungseinheit, umgelegt auf die enthaltene Stueckzahl.
 *
 * DIESE UMLAGE RECHNET NICHT DIESE DATEI. Sie steht als `perUnitG` bereits im
 * Vertrag und wird in der Datenbank gerechnet (`core/logistics.ts`). Hier wird
 * sie nur an die Stelle gestellt, an der die Frage gestellt wird.
 *
 * WARUM DER BLOCK AUCH DANN STEHT, WENN NICHTS ERFASST IST.
 *
 * Am Referenzartikel HB200-U4KD2D-85-CRI ist kein Materialgewicht hinterlegt
 * -- in der Masterliste sind die beiden Spalten bei allen dreizehn Produkten
 * leer. Bisher fuehrte das zu gar keiner Aussage: die Ebene stand in der
 * Tabelle, die Materialzeile fehlte einfach. Wer die Verpackung las, konnte
 * nicht unterscheiden zwischen „kein Material noetig" und „noch niemand hat
 * es gewogen".
 *
 * UND DER REST DER BILANZ IST KEIN MATERIALGEWICHT.
 *
 * Direkt darueber steht „6,8 kg − 5,5 kg = 1,3 kg nicht zugeordnet". Genau
 * diese Zahl darf niemand als Kartongewicht lesen; sie kann Einlagen,
 * Zubehoer, Feuchtigkeit und Messabweichung enthalten. Der Hinweis sagt das
 * ausdruecklich -- an der Stelle, an der die Verwechslung entstuende.
 *
 * KEIN ROT, KEINE FLAECHE, KEIN „UNVOLLSTAENDIG". Ein Satz, der die betroffene
 * Ebene benennt.
 */
const runde = (n: number): number => Math.round(n * 1000) / 1000;

/*
 * `restVonEbene` IST FORT (§3/§5).
 *
 * Sie uebersetzte `PackagingLevel` in `BilanzEbene` und fragte
 * `abgeleiteterRest`, ob aus dem Rest eine Materialaussage werden
 * darf. Diese Frage stellt sich nicht mehr: der Rest ist eine
 * Eingabedifferenz und wird nie eine Materialaussage.
 *
 * Damit faellt auch die Sonderregel, die daran hing -- eine Ebene
 * ohne gewogenes Material galt als vollstaendig, sobald ihr Rest
 * zugeordnet war. Jetzt gilt wieder das Einfache: wer Material
 * erfasst hat, hat eine Materialaussage; wer keines erfasst hat,
 * nicht. Am TP154 steht der Masterkarton deshalb bei „hier fehlt noch
 * etwas", und die Gewichtsbilanz nennt die 800 g als Differenz.
 */

/**
 * MATERIAL JE VERKAUFTEM ARTIKEL -- die Groesse, die Vera braucht.
 *
 * `PRODUCT_DATA_MODEL.md` §19.1 nennt als fachlichen Mindestbedarf von Vera V1
 * genau drei Angaben: Produktgewicht, Pappe je Produkt, Kunststoff je Produkt.
 * §19.2 nennt auch den Weg dorthin -- das Materialgewicht einer vollstaendigen
 * Verpackungseinheit, umgelegt auf die enthaltene Stueckzahl.
 *
 * ZWEI HERKUENFTE, ZWEI WOERTER.
 *
 *   erfasst      eine gespeicherte Materialzeile
 *   abgeleitet   der Rest der Gewichtsbilanz, weil jemand bestaetigt hat,
 *                woraus er besteht
 *
 * „gemessen" steht hier bewusst NICHT. `packaging_level_materials` weiss
 * nicht, ob jemand gewogen oder aus einem Datenblatt abgeschrieben hat --
 * das Wort waere selbst eine Behauptung ohne Beleg.
 *
 * WARUM DER BLOCK AUCH DANN STEHT, WENN NICHTS ERFASST IST.
 *
 * Am Referenzartikel ist kein Materialgewicht hinterlegt -- in der Masterliste
 * sind die beiden Spalten bei allen dreizehn Produkten leer. Bisher fuehrte
 * das zu gar keiner Aussage: die Ebene stand in der Tabelle, die Materialzeile
 * fehlte einfach. Wer die Verpackung las, konnte nicht unterscheiden zwischen
 * „kein Material noetig" und „noch niemand hat es gewogen".
 *
 * UND DER REST DER BILANZ IST KEIN MATERIALGEWICHT.
 *
 * Direkt darueber steht „6,8 kg − 5,5 kg = 1,3 kg nicht zugeordnet". Genau
 * diese Zahl darf niemand als Kartongewicht lesen; sie kann Einlagen,
 * Zubehoer, Feuchtigkeit und Messabweichung enthalten. Der Hinweis sagt das
 * ausdruecklich -- an der Stelle, an der die Verwechslung entstuende.
 *
 * KEIN ROT, KEINE FLAECHE, KEIN „UNVOLLSTAENDIG". Ein Satz, der die betroffene
 * Ebene benennt.
 */
/**
 * Die Box „Verpackung" -- was ueber die einzelne Ebene hinausgeht.
 *
 * Hier stehen die Aussagen, die sich aus ALLEN Ebenen zusammen ergeben: die
 * Gewichtsbilanz (was vom Bruttogewicht die Verpackung ist) und das Material
 * je verkauftem Artikel -- die Groesse, die Vera braucht. Beides ist
 * gerechnet und traegt deshalb kein Eingabefeld; die Zahlen, aus denen es
 * entsteht, stehen in den Ebenenboxen darueber.
 *
 * Die Restmaterialbestaetigung bleibt, was sie ist: eine eigene fachliche
 * Handlung mit eigenem Recht (`logistics.confirm`), kein Nebenprodukt des
 * Speicherns.
 */
function VerpackungsBox({ levels, aktuell, produktgewicht, meta, mayConfirm, i18n,
                          onChanged, onError }: {
  levels: readonly PackagingLevel[];
  aktuell: boolean;
  produktgewicht: string | null;
  meta: MetaResponse;
  mayConfirm: boolean;
  i18n: I18n;
  onChanged: (r: SupplierArticle) => void;
  onError: (e: unknown) => void;
} & Partial<Bearbeitung>): React.JSX.Element {
  return (
    <section className="log__group log__group--verpackung">
      <h4 className="log__grouptitle">{i18n.t("log.groupPackaging")}</h4>
      <Gewichtsbilanz levels={levels} produktgewicht={produktgewicht} i18n={i18n} />
      <MaterialJeArtikel levels={levels} aktuell={aktuell}
        produktgewicht={produktgewicht} meta={meta}
        mayConfirm={mayConfirm} i18n={i18n}
        onChanged={onChanged} onError={onError} />
    </section>
  );
}

function MaterialJeArtikel({ levels, aktuell, produktgewicht, meta, mayConfirm, i18n,
                             onChanged, onError }: {
  levels: readonly PackagingLevel[];
  /** Nur der geltende Stand fragt nach fehlenden Angaben -- siehe unten. */
  aktuell: boolean;
  /** Aus dem Merkmal `weight`, in Kilogramm. Ohne ihn keine Bilanz. */
  produktgewicht: string | null;
  meta: MetaResponse;
  /** `logistics.write` UND `logistics.confirm`. Entschieden wird im Server. */
  mayConfirm: boolean;
  i18n: I18n;
  onChanged: (r: SupplierArticle) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  /*
   * DIE SUMME UEBER ALLE STUFEN -- und der zugeordnete Rest gehoert dazu.
   *
   * `m.perUnitG` aus der Datenbank ist je EBENE richtig: `unit_count` zaehlt
   * die Verkaufseinheiten (Migration 0012), also teilt es bereits durch die
   * Zahl der Produkte. Was dort fehlt, ist die Summe -- am TP154 traegt der
   * Innenkarton 266 g Pappe und der Masterkarton weitere 133,33 g, und nur
   * beide zusammen sind die Groesse, nach der die Abgabe berechnet wird.
   * Der Masteranteil steht ausserdem gar nicht in `perUnitG`: er ist der
   * gerechnete Rest und keine erfasste Materialzeile.
   */
  const jeProdukt = materialJeProdukt(levels, produktgewicht);
  const bilanzen = new Map(
    verpackungsbilanz(levels, produktgewicht).map((b) => [b.ebene.id, b]));
  /*
   * EINE EBENE ZAEHLT AUCH OHNE GEWOGENES MATERIAL.
   *
   * Hier stand `.filter((x) => x.l.materials.length > 0)`. Am TP154 fiel damit
   * genau der Masterkarton heraus: an ihm ist nichts gewogen, seine 800 g sind
   * vollstaendig zugeordnete Kartonage. Die 133,33 g Pappe je Produkt standen
   * dadurch nirgends -- obwohl sie in die Abgabe eingehen.
   */
  const zeilen = levels.map((l) => ({
      l,
      produkte: bilanzen.get(l.id)?.produkteJeEinheit ?? l.unitCount,
    }))
    .filter((x) => x.l.materials.length > 0);
  /*
   * Der Hinweis gilt nur dem AKTUELLEN Stand.
   *
   * Ein abgeloester Stand beschreibt eine Verpackung, die es so nicht mehr
   * gibt; „hier fehlt noch etwas" waere dort eine Aufforderung, ein Archiv zu
   * pflegen. Was an ihm ERFASST ist, bleibt dagegen sichtbar -- fuer eine
   * Periode in der Vergangenheit ist genau dieser Stand der richtige.
   */
  /* Ohne gewogenes Material fehlt die Materialaussage -- und das steht
     da. Eine zugeordnete Differenz ersetzt sie nicht (§3). */
  const ohne = aktuell ? levels.filter((l) => l.materials.length === 0) : [];
  if (zeilen.length === 0 && ohne.length === 0) return null;

  const gramm = (n: number): string =>
    n.toLocaleString(i18n.lang, { maximumFractionDigits: 2 });

  return (
    <div className="log__material">
      <h4 className="log__materialtitel">{t("log.materialPerUnit")}</h4>
      {/*
        * DIE SUMME STEHT OBEN.
        *
        * Vera braucht genau zwei Zahlen: Pappe je Produkt und Kunststoff je
        * Produkt. Am TP154 sind das 399,33 g und 33,00 g -- und beide entstehen
        * erst, wenn man den Innenkarton und den Masterkarton zusammenzaehlt.
        * Wer nur die Ebenen untereinander sieht, muss diese Addition im Kopf
        * machen; genau dabei entstehen die Zahlen, die spaeter niemand
        * nachvollziehen kann. Die Herleitung steht darunter und bleibt lesbar.
        */}
      {jeProdukt.length > 0 && (
        <ul className="log__materialsumme">
          {jeProdukt.map((m) => (
            <li key={m.material}>
              <span className="log__materialname">{i18n.tCode("packMaterial", m.material)}</span>
              <strong className="log__materialwert">{gramm(m.grammJeProdukt)} g</strong>
              <span className="log__materialherkunft">
                {m.anteile.length > 1
                  ? m.anteile.map((a) =>
                      `${i18n.tCode("packLevel", a.stufe)} ${gramm(a.grammJeProdukt)} g`).join(" + ")
                  : t(m.anteile[0]!.herkunft === "abgeleitet"
                      ? "log.materialDerived" : "log.materialRecorded")}
              </span>
            </li>
          ))}
        </ul>
      )}
      {zeilen.map(({ l, produkte }) => (
        <div key={l.id} className="log__materialebene">
          <p className="log__materialkopf">
            {t("log.materialFrom", { level: i18n.tCode("packLevel", l.type), n: produkte })}
          </p>
          <ul className="log__materialliste">
            {l.materials.map((m) => (
              /*
                * ── NUR ERFASSTES MATERIAL (§3) ───────────────────
                *
                * HIER WURDE DER REST AUF DAS MATERIAL ADDIERT --
                * „erfasst 266 g + Rest 1 g = 267 g" in einer Zeile.
                * Das war die Folge von §14 des Vorblocks und ist mit
                * §3 aufgehoben: die Differenz ist kein Materialwert,
                * also darf sie auch in keiner Materialzeile stehen.
                *
                * Was hier steht, ist jetzt ausschliesslich gewogen.
                * Was fehlt, steht als Differenz in der Gewichtsbilanz
                * -- einmal, an der Stelle, an der es korrigiert wird.
                */
              <li key={m.material}>
                <span className="log__materialname">{i18n.tCode("packMaterial", m.material)}</span>
                <strong className="log__materialwert">
                  {gramm(runde(Number(m.weightG) / produkte))} g
                </strong>
                <span className="log__materialherkunft">{t("log.materialRecorded")}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {ohne.map((l) => (
        <p key={l.id} className="log__materialoffen">
          {t("log.materialMissing", { level: i18n.tCode("packLevel", l.type) })}
        </p>
      ))}
    </div>
  );
}

/*
 * ── DIE RESTMATERIALBESTAETIGUNG IST FORT (§5) ──────────────────────
 *
 * HIER STAND `Restbestaetigung`: ein Auswahlfeld mit den zulaessigen
 * Materialien, eine Schaltflaeche „Restmaterial bestaetigen" und eine
 * zweite zum Zuruecknehmen. Der Rest wurde damit einem Material
 * zugerechnet und als Anteil mitgezaehlt.
 *
 * DIESER WEG IST ALS NUTZERWORKFLOW AUFGEHOBEN. Der Grund steht in §5
 * und ist kein Gestaltungsgrund: der Nutzer soll die eigentlichen
 * Materialwerte korrekt eingeben, nicht einen Fehler nachtraeglich
 * verteilen. Eine Differenz ist eine Eingabedifferenz; sie wird
 * korrigiert oder neu gemessen (§2 A/B), nicht zugeordnet.
 *
 * WAS GEBLIEBEN IST:
 *
 *   * die Differenz selbst -- sie steht in der Gewichtsbilanz, mit
 *     Verpackungsgewicht, Materialsumme und Hinweis (§3)
 *   * die Sperre -- ohne Differenz 0 g kein belastbarer Stand (§4)
 *   * `residual_material_id` in der Datenbank, unangetastet, und der
 *     Vermerk „frueher zugeordnet" an den zwei Stufen, die eine
 *     Zuordnung tragen (§6)
 *
 * Der Schreibweg des Servers (`residualMaterial` an
 * `updatePackagingLevel`) ist NICHT entfernt: er traegt die
 * Ruecknahme bestehender Zuordnungen und wird von der Oberflaeche
 * nicht mehr angeboten. Eine Schemaaenderung waere ein eigener Block.
 */

/**
 * Eine Verpackungsebene.
 *
 * `unitCount` ist die Zahl der VERKAUFSEINHEITEN in einer Einheit dieser
 * Ebene. Ein `master` mit 1 ist ein Einzelversandkarton -- er wird hier
 * genauso dargestellt wie ein Sammelkarton, ohne Hinweis und ohne Markierung.
 */
/**
 * Von innen nach aussen: Produkt, Innenverpackung, Masterkarton.
 *
 * Die Datenbank liefert die Ebenen in Anlagereihenfolge. Die Anschauung
 * verlangt die physische -- was worin steckt.
 */
const rang = (typ: string): number => (typ === "inner" ? 1 : 2);

/**
 * Die Produktzeile der Verpackungshierarchie.
 *
 * Sie zeigt, was in der Innenverpackung steckt: Laenge beziehungsweise
 * Durchmesser, Breite, Hoehe und Gewicht. Fehlt ein Mass, bleibt die Zelle
 * leer; erfunden wird nichts.
 *
 * ZWEI QUELLEN, EINE RANGFOLGE
 *
 *   1. Das MERKMAL des Artikels. Das ist das gueltige Mass, und dort wird es
 *      auch gepflegt. Diese Ansicht liest es nur.
 *   2. Die ANNAHME des Verpackungsstandes (`packaging_versions.product_*`).
 *      Sie sagt, wofuer diese Verpackung ausgelegt wurde -- eine Aussage des
 *      Lieferanten, kein Produktmerkmal.
 *
 * Ist das Merkmal da, gilt es. Fehlt es, wird die Annahme gezeigt und als
 * solche benannt -- sie zu verschweigen waere Informationsverlust, sie
 * unbeschriftet zu zeigen waere eine Verwechslung. Beide Faelle bleiben eine
 * Ansicht: hier wird nichts gespeichert und nichts gespiegelt.
 *
 * Bei einem runden Produkt traegt die erste Spalte den DURCHMESSER. Sie heisst
 * deshalb „Laenge / Ø", und die Breite bleibt frei -- ein Zylinder hat keine.
 */
function ProduktBox({ masse, stand, i18n }: {
  masse: Produktmasse | null;
  stand: { laenge: string | null; breite: string | null; hoehe: string | null };
  i18n: I18n;
}): React.JSX.Element | null {
  const ausMerkmal = masse !== null
    && (masse.laenge !== null || masse.durchmesser !== null
        || masse.breite !== null || masse.hoehe !== null || masse.gewicht !== null);
  const ausStand = stand.laenge !== null || stand.breite !== null || stand.hoehe !== null;
  if (!ausMerkmal && !ausStand) return null;

  const w = ausMerkmal
    ? { laenge: masse!.laenge, durchmesser: masse!.durchmesser, breite: masse!.breite,
        hoehe: masse!.hoehe, gewicht: masse!.gewicht }
    : { laenge: stand.laenge, durchmesser: null, breite: stand.breite,
        hoehe: stand.hoehe, gewicht: null };

  const rund = w.durchmesser !== null;
  /*
   * Dezimaltrennzeichen der Anzeigesprache.
   *
   * Die Merkmalswerte kommen als Zeichenkette an, damit unterwegs keine
   * Genauigkeit verloren geht -- mit dem Punkt der Datenbank. In derselben
   * Tabelle stand damit „5.5 kg" ueber einer Bilanz mit „5,500 kg". Zwei
   * Schreibweisen fuer dieselbe Zahl, drei Zeilen auseinander.
   *
   * Umgerechnet wird nichts, nur geschrieben: aus der Zeichenkette wird eine
   * Zahl und wieder eine Zeichenkette, ohne feste Nachkommastellen -- 400
   * bleibt 400, 5.5 wird 5,5.
   */
  const zahl = (v: string): string => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString(i18n.lang) : v;
  };
  const mm = (v: string | null): React.ReactNode => v === null ? <Unknown /> : `${zahl(v)} mm`;

  return (
    <section className="log__group log__group--produkt">
      <h4 className="log__grouptitle">{i18n.t("log.levelProduct")}</h4>
      {!ausMerkmal && (
        /* Kein Merkmal vorhanden -- die Zahl stammt aus dem Verpackungsstand.
           Wer sie fuer eine gepflegte Produktangabe haelt, irrt sich; also
           steht es dabei. */
        <p className="log__perunit">{i18n.t("log.dimsFromRevision")}</p>
      )}
      <div className="log__rows">
        <LogZeile label={rund ? i18n.t("log.dimDiameter") : i18n.t("log.dimLength")}>
          {rund ? `Ø ${zahl(w.durchmesser!)} mm` : mm(w.laenge)}
        </LogZeile>
        {!rund && <LogZeile label={i18n.t("log.dimWidth")}>{mm(w.breite)}</LogZeile>}
        <LogZeile label={i18n.t("log.dimHeight")}>{mm(w.hoehe)}</LogZeile>
        {/*
          * ── DAS PRODUKTGEWICHT IST EIN NETTOGEWICHT (§5) ──────────
          *
          * HIER STAND „Bruttogewicht", und das war fachlich falsch --
          * nicht nur in der Benennung, sondern IM WIDERSPRUCH ZUR
          * EIGENEN RECHNUNG dieser Ansicht.
          *
          * `verpackungsbilanz(ebenen, produktgewichtKg)` zieht genau
          * diesen Wert als INHALT vom Bruttogewicht des Kartons ab,
          * um das Verpackungsgewicht zu erhalten. Wer ihn als brutto
          * liest, liest die Verpackung zweimal: einmal im Produkt und
          * einmal als Differenz.
          *
          * Am TP154 sind das 2,75 kg -- die nackte Leuchte,
          * vollstaendig ausgepackt: keine Innenverpackung, kein
          * Innenkarton, kein Masterkarton, keine Folie.
          *
          * BRUTTOGEWICHTE BEGINNEN AUF VERPACKUNGSEBENE. Innenkarton
          * und Masterkarton tragen sie weiter, und zwar zu Recht:
          * dort ist die Verpackung Teil des Gewogenen.
          */}
        <LogZeile label={i18n.t("log.netWeight")}>
          {w.gewicht === null ? <Unknown /> : `${zahl(w.gewicht)} kg`}
          <span className="log__nettohinweis">{i18n.t("log.netWeightHint")}</span>
        </LogZeile>
      </div>
      {/*
        * DIESE BOX HAT KEINEN STIFT -- und das ist keine Luecke.
        *
        * Die Produktmasse sind MERKMALSWERTE. Sie stehen hier, weil man sie
        * beim Lesen der Verpackung braucht; geaendert werden sie dort, wo sie
        * gefuehrt werden. Ein zweites Eingabefeld waere eine zweite Wahrheit
        * -- genau die, die `packaging_versions.product_length_mm` ausdruecklich
        * NICHT sein soll („Ersetzt KEIN Produktmerkmal", Migration 0012).
        */}
      <p className="log__hint">{i18n.t("log.productDimsHint")}</p>
    </section>
  );
}

/** Eine Zeile in einer Logistikbox: Beschriftung links, Wert rechts. */
function LogZeile({ label, children }: {
  label: string; children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="log__item">
      <span className="log__label">{label}</span>
      <span className="log__vals">{children}</span>
    </div>
  );
}

/**
 * Was von einem Bruttogewicht die Verpackung ist.
 *
 * DIE RECHNUNG
 *
 *     Bruttogewicht  -  (Produktgewicht x Stueckzahl)  =  Verpackungsgewicht
 *
 * Sie ist trivial und trotzdem die einzige Stelle, an der jemand merkt, dass
 * ein Bruttogewicht nicht stimmen kann: faellt der Rest negativ aus, ist eine
 * der drei Zahlen falsch. Das wird gesagt, nicht auf 0 gerundet.
 *
 * WARUM DER REST NICHT „KARTON/PAPPE" HEISST
 *
 * Weil niemand bestaetigt hat, dass es nur Karton ist. `packaging_versions`
 * kennt kein Feld, das „diese Materialaufstellung ist vollstaendig" sagen
 * koennte, und `packaging_level_materials` ist im gesamten Bestand leer. Der
 * Rest ist damit genau das: der Rest. Ihn „Karton/Pappe" zu nennen waere eine
 * Behauptung ueber Material, fuer die es keine Quelle gibt -- und aus der
 * spaeter eine VerpackungsG-Meldung wuerde.
 *
 * Sobald ein Material erfasst ist, wird es abgezogen und benannt; was dann
 * noch bleibt, bleibt weiterhin „nicht zugeordnet".
 */
function Gewichtsbilanz({ levels, produktgewicht, i18n }: {
  levels: readonly PackagingLevel[];
  produktgewicht: string | null;
  i18n: I18n;
}): React.JSX.Element | null {
  const t = i18n.t;
  /* Ohne Produktgewicht gibt es nichts zu verrechnen. Eine Bilanz, die das
     Produkt mit 0 kg ansetzt, waere schlimmer als keine: sie erklaerte das
     gesamte Bruttogewicht zur Verpackung. */
  if (produktgewicht === null) return null;

  /*
   * DIE RECHNUNG STEHT IN `shared` -- HIER WIRD SIE NUR GEZEIGT.
   *
   * Sie zog frueher von JEDER Ebene das Produktgewicht ab. Am TP154 ergab das
   * fuer den Masterkarton 19,10 kg − 6 × 2,75 kg = 2,60 kg Verpackung; darin
   * steckten sechsmal die Innenverpackung, die eine Ebene tiefer schon
   * gezaehlt war. Richtig sind 0,80 kg. Die Regel -- immer die DIREKT
   * enthaltene Einheit abziehen -- steht jetzt an einer Stelle, weil sie an
   * drei Stellen gebraucht wird.
   */
  const zeilen = verpackungsbilanz(levels, produktgewicht);
  if (zeilen.length === 0) return null;

  /*
   * Gramm sind die feinste Angabe, die hier vorkommt -- Materialgewichte
   * stehen in Gramm. Mehr als drei Nachkommastellen waeren also erfunden.
   *
   * Nachlaufende Nullen fallen weg: „6,800 kg − 5,500 kg = 1,300 kg" behauptet
   * eine Genauigkeit auf das Gramm, die in „6,8 kg" gar nicht steckt.
   */
  const kg = (n: number): string =>
    `${n.toLocaleString(i18n.lang, { maximumFractionDigits: 3 })} kg`;
  const gramm = (n: number): string =>
    n.toLocaleString(i18n.lang, { maximumFractionDigits: 2 });

  return (
    <div className="log__bilanz">
      <h4 className="log__bilanztitel">{t("log.weightBalance")}</h4>
      {zeilen.map((b) => {
        const l = b.ebene;
        const [brutto, inhalt, erfasst, rest] =
          [b.bruttoG / 1000, b.inhaltG / 1000, b.erfasstG / 1000, b.restG / 1000];
        return (
        <p key={l.id} className="log__bilanzzeile">
          <span className="log__bilanzebene">{i18n.tCode("packLevel", l.type)}</span>
          <span>
            {/* Die Beschriftung NENNT den Inhalt. „− 18,3 kg Inhalt" laesst
                offen, was da gewogen wurde; „6 × Innenkarton" sagt es. Genau
                diese Auskunft fehlte, als die Rechnung noch falsch war. */}
            {kg(brutto)} − {kg(inhalt)}{" "}
            {b.inhaltQuelle === "produkt"
              ? t("log.balanceContent", { n: b.inhaltStueck })
              : t("log.balanceContentLevel",
                  { n: b.inhaltStueck, level: i18n.tCode("packLevel", b.inhaltQuelle) })}
            {" = "}
            <strong className={b.verpackungG < 0 ? "log__bilanzfehler" : undefined}>
              {gramm(b.verpackungG)} g
            </strong>{" "}
            {t("log.balancePackagingWeight")}
            {/*
              * ── DIE DIFFERENZ, NICHT DER REST (§3) ──────────────────
              *
              * HIER STAND DER REST MIT SEINEM MATERIALNAMEN: war eine
              * Restmaterialzuordnung bestaetigt, hiess die Luecke nach
              * dem Material („= 1,06 kg Pappe"), sonst „nicht
              * zugeordnet". Beides behandelte sie als einen ZUSTAND,
              * den man haben kann.
              *
              * Seit §2 ist sie keiner: eine Differenz ist eine
              * Eingabedifferenz, und sie wird korrigiert, nicht
              * benannt. Deshalb steht hier jetzt, was §3 verlangt --
              * Verpackungsgewicht, Materialsumme, Differenz -- und
              * kein Materialwort.
              *
              * EIN NEGATIVES VERPACKUNGSGEWICHT bleibt ein Widerspruch
              * in den Messwerten und wird weiter als solcher gemeldet.
              */}
            <span className="log__bilanzmaterial">
              {" · "}{t("log.balanceMaterialSum")} {gramm(b.erfasstG)} g
              {b.restG === 0 ? (
                <span className="log__bilanzstimmt">
                  {" · "}{t("log.balanceMatches")}
                </span>
              ) : (
                <span className="log__bilanzdifferenz">
                  {" · "}{t("log.balanceDifference")}{" "}
                  {b.restG > 0 ? "+" : "−"}{gramm(Math.abs(b.restG))} g
                </span>
              )}
            </span>
            {b.verpackungG < 0 && <> {t("log.balanceImpossible")}</>}
            {/*
              * ── EINE ALTE ZUORDNUNG BLEIBT LESBAR (§6) ──────────────
              *
              * Zwei Stufen im Bestand tragen eine bestaetigte
              * Restmaterialzuordnung, beide am TP154. Sie wird NICHT
              * umgeschrieben und NICHT mitgerechnet -- sie steht als
              * Vermerk da, damit nachvollziehbar bleibt, was einmal
              * entschieden wurde.
              */}
            {l.residualMaterial !== null && (
              <span className="log__restverlauf" data-testid="log-restverlauf">
                {" · "}{t("log.residualHistoric",
                  { material: i18n.tCode("packMaterial", l.residualMaterial) })}
              </span>
            )}
          </span>
        </p>
      ); })}
      {/*
        * ── DER HINWEIS STEHT EINMAL (§3) ──────────────────────────────
        *
        * Er gilt fuer den Stand und nicht fuer eine Stufe; an jeder
        * Zeile wiederholt waere er dreimal derselbe Satz. Er sagt, was
        * zu tun ist -- korrigieren oder neu messen -- und nicht nur,
        * dass etwas nicht stimmt.
        */}
      {zeilen.some((b) => b.restG !== 0) && (
        <p className="log__bilanzhinweis" role="status" data-testid="log-bilanzhinweis">
          {t("log.balanceMismatchHint")}
        </p>
      )}
    </div>
  );
}

/**
 * Eine Verpackungsebene als Box -- Masterkarton oder Innenkarton.
 *
 * DIESELBE BOX IN BEIDEN MODI. Im Lesemodus steht rechts die Zahl, im
 * Bearbeitungsmodus ein Feld an genau derselben Stelle. Die Zeilenfolge
 * aendert sich nicht, die Beschriftungen bleiben stehen, die Box behaelt
 * ihren Platz im Raster.
 *
 * DER INNENKARTON ERSCHEINT NUR, WENN ES IHN GIBT. Er ist eine Zeile in
 * `packaging_levels` mit `level_type = 'inner'`; hat der Artikel keine,
 * gibt es die Box nicht. Eine leere Box „Innenkarton — nicht bestimmt"
 * waere an einem Produkt, das einzeln im Masterkarton liegt, eine Frage,
 * die sich nicht stellt. Angelegt wird eine Ebene weiterhin ueber den
 * Verpackungsstand, nicht durch ein Formular in dieser Box.
 */
/**
 * Die Bilanz WAEHREND DES TIPPENS -- aus dem Entwurf, nicht aus dem Gespeicherten.
 *
 * Wer ein Bruttogewicht korrigiert, will sehen, was daraus folgt, BEVOR er
 * speichert. Stuende hier der gespeicherte Stand, zeigte die Maske waehrend
 * der ganzen Eingabe die Zahlen, die gerade widerlegt werden -- und die
 * Korrektur waere erst nach dem Speichern zu beurteilen, also genau dann,
 * wenn sie schon in der Historie steht.
 *
 * Ungetippte Felder fallen auf den gespeicherten Wert zurueck; ein geleertes
 * Feld ist `null` und damit NICHT BESTIMMT -- niemals 0.
 */
function entwurfsEbenen(
  levels: readonly PackagingLevel[],
  entwurf: Record<number, Ebenenfeld>, material: Record<number, Materialfeld>,
): BilanzEbene[] {
  const zahl = (v: string): string | null => {
    const x = v.trim().replace(",", ".");
    return x === "" || !Number.isFinite(Number(x)) ? null : x;
  };
  return levels.map((l) => {
    const e = entwurf[l.id] ?? ausEbene(l);
    const m = material[l.id] ?? ausMaterial(l);
    const anzahl = Number(e.anzahl.trim());
    return {
      id: l.id, type: l.type,
      grossWeightKg: zahl(e.gewicht),
      unitCount: Number.isInteger(anzahl) && anzahl > 0 ? anzahl : l.unitCount,
      materials: Object.entries(m)
        .flatMap(([code, g]) => {
          const w = zahl(g);
          return w === null || !(Number(w) > 0) ? [] : [{ material: code, weightG: w }];
        }),
      residualMaterial: l.residualMaterial,
    };
  });
}

function EbenenBox({ level, levels, produktgewicht, bearbeitet, materialien, entwurf,
                     material, fehler, speichert, setEbene, setMaterial }: {
  level: PackagingLevel;
  levels: readonly PackagingLevel[];
  produktgewicht: string | null;
} & Bearbeitung): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const e = entwurf[level.id] ?? ausEbene(level);
  const m = material[level.id] ?? ausMaterial(level);
  const offen = !ebeneGleich(e, ausEbene(level)) || !materialGleich(m, ausMaterial(level));
  const fehlt = fehler[level.id];
  const bilanz = ebenenbilanz(
    entwurfsEbenen(levels, entwurf, material), level.id, produktgewicht);
  const gramm = (n: number): string =>
    `${n.toLocaleString(i18n.lang, { maximumFractionDigits: 2 })} ${t("unit.gram")}`;

  /*
   * LEEREN UND ZURUECKSETZEN SIND ZWEI VERSCHIEDENE HANDLUNGEN.
   *
   *   Leeren          alle Werte dieser Ebene auf NICHT BESTIMMT -- eine
   *                   fachliche Aussage: wir wissen es nicht mehr.
   *   Zuruecksetzen   den Entwurf verwerfen und zum GESPEICHERTEN Stand
   *                   zurueck -- eine Bedienhandlung ohne fachliche Folge.
   *
   * Beide brauchen ihren eigenen Knopf. Gaebe es nur einen, hiesse er
   * „Zuruecksetzen" und taete mal das eine, mal das andere -- und wer sich
   * vertippt hat, loeschte damit ein gewogenes Gewicht.
   *
   * Die Stueckzahl wird NICHT geleert. Sie ist in der Datenbank NOT NULL und
   * groesser null (Migration 0012), und ohne sie ist keine einzige Bilanz
   * dieser Ebene definiert -- eine 1 an ihrer Stelle waere kein Leerwert,
   * sondern eine geratene Zahl.
   */
  const leeren = (): void => {
    setEbene(level.id, { laenge: "", breite: "", hoehe: "", gewicht: "" });
    for (const code of materialien) setMaterial(level.id, code, "");
  };
  const zuruecksetzen = (): void => {
    const g = ausEbene(level);
    setEbene(level.id, g);
    for (const code of materialien) setMaterial(level.id, code, ausMaterial(level)[code] ?? "");
  };

  const feld = (wert: string, k: keyof Ebenenfeld, label: string): React.JSX.Element => (
    <input className="log__input" inputMode="decimal" value={wert} disabled={speichert}
      aria-label={`${i18n.tCode("packLevel", level.type)} — ${label}`}
      onChange={(ev) => setEbene(level.id, { [k]: ev.target.value })} />
  );
  /* Lesen: die Zahl mit ihrer Einheit. Bearbeiten: das Feld an derselben
     Stelle. Eine Funktion fuer beides, damit die Zeile nicht zweimal
     beschrieben wird und die beiden Fassungen nicht auseinanderlaufen. */
  const zeile = (label: string, wert: string | null, einheit: string,
                 k: keyof Ebenenfeld): React.JSX.Element => (
    <LogZeile label={label}>
      {bearbeitet
        ? <>{feld(e[k], k, label)} <span className="log__unit">{einheit}</span></>
        : wert === null ? <Unknown /> : `${wert} ${einheit}`}
    </LogZeile>
  );

  const klassen = ["log__group", "log__level"];
  if (offen) klassen.push("log__level--geaendert");
  if (fehlt !== undefined) klassen.push("log__level--fehler");

  return (
    <section className={klassen.join(" ")}>
      <h4 className="log__grouptitle">{i18n.tCode("packLevel", level.type)}</h4>
      <div className="log__rows">
        {zeile(t("log.dimLength"), level.lengthMm, "mm", "laenge")}
        {zeile(t("log.dimWidth"), level.widthMm, "mm", "breite")}
        {zeile(t("log.dimHeight"), level.heightMm, "mm", "hoehe")}
        <LogZeile label={t("log.unitCount")}>
          {bearbeitet ? feld(e.anzahl, "anzahl", t("log.unitCount")) : level.unitCount}
        </LogZeile>
        {zeile(t("log.grossWeight"), level.grossWeightKg, "kg", "gewicht")}
        {/*
          * BERECHNET BLEIBT BERECHNET -- in beiden Modi als Text.
          *
          * `volume_m3` ist in der Datenbank GENERATED und folgt den
          * Aussenmassen. Ein Eingabefeld waere eine zweite Wahrheit, die bei
          * der naechsten Massaenderung still falsch wird. Bis zum Speichern
          * steht hier der ALTE Wert -- gerechnet wird in der Datenbank und
          * nicht im Browser, und ein im Browser vorausgerechneter Wert waere
          * genau die zweite Wahrheit, die es zu vermeiden gilt.
          */}
        <LogZeile label={t("log.cbm")}>
          <span className="log__num--berechnet">{formatCbm(level.volumeM3)}</span>
        </LogZeile>
        <LogZeile label={t("log.cbmPerUnit")}>
          <span className="log__num--berechnet">{formatCbm(level.cbmPerUnit)}</span>
        </LogZeile>
        {/*
          * DIE BILANZ STEHT AN DER EBENE, ZU DER SIE GEHOERT.
          *
          * Sie ist gerechnet und traegt deshalb dieselbe Darstellung wie das
          * CBM: Text, kein Eingabefeld. Am Masterkarton sagt sie mit, WOVON
          * abgezogen wurde -- „6 × Innenkarton" und nicht „Inhalt". Genau
          * diese Auskunft fehlte, als dieselbe Stelle sechsmal das
          * Produktgewicht abzog und 1,8 kg zu viel Verpackung auswies.
          */}
        {bilanz !== null && (
          <>
            <LogZeile label={t("log.packagingWeight")}>
              <span className="log__num--berechnet">{gramm(bilanz.verpackungG)}</span>
              <span className="log__herleitung">
                {" "}{bilanz.inhaltQuelle === "produkt"
                  ? t("log.balanceContent", { n: bilanz.inhaltStueck })
                  : t("log.balanceContentLevel", { n: bilanz.inhaltStueck,
                      level: i18n.tCode("packLevel", bilanz.inhaltQuelle) })}
              </span>
            </LogZeile>
            {/* Dieselbe Entkopplung wie an der Rasterzeile (MICRO 001):
                bei Differenz 0 keine Zeile, sonst nur der Betrag --
                und kein Materialname aus `residual_material_id`. */}
            {bilanz.restG !== 0 && (
              <LogZeile label={t("log.residualWeight")}>
                <span className={bilanz.restG < 0
                  ? "log__num--berechnet log__bilanzfehler" : "log__num--berechnet"}>
                  {gramm(bilanz.restG)}
                </span>
                {bilanz.restG < 0 && (
                  <span className="log__herleitung">
                    {" "}{t("log.balanceImpossible")}
                  </span>
                )}
              </LogZeile>
            )}
          </>
        )}
      </div>

      {/*
        * DIE MATERIALIEN DIESER EBENE.
        *
        * Im Lesemodus nur, was erfasst ist. Im Bearbeitungsmodus der ganze
        * Wertevorrat -- was fehlt, liesse sich sonst nicht ergaenzen. Ein
        * leeres Feld heisst „nichts erfasst" und nicht „null Gramm".
        */}
      {!bearbeitet && level.materials.length > 0 && (
        <ul className="log__materials">
          {level.materials.map((x) => (
            <li key={x.material}>
              {i18n.tCode("packMaterial", x.material)} {x.weightG} {t("unit.gram")}
            </li>
          ))}
        </ul>
      )}
      {bearbeitet && (
        <ul className="log__materials log__materials--edit">
          {materialien.map((code) => (
            <li key={code}>
              <label>
                <span>{i18n.tCode("packMaterial", code)}</span>
                <input className="log__input log__input--g" inputMode="decimal"
                  value={m[code] ?? ""} disabled={speichert}
                  aria-label={`${i18n.tCode("packMaterial", code)} — ${t("log.materialWeight")}`}
                  onChange={(ev) => setMaterial(level.id, code, ev.target.value)} />
                <span className="log__unit">{t("unit.gram")}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {bearbeitet && (
        <p className="log__levelaktionen">
          <button className="btn log__inline" disabled={speichert} onClick={leeren}>
            {t("log.levelClear")}
          </button>
          <button className="btn log__inline" disabled={speichert || !offen}
            onClick={zuruecksetzen}>{t("log.levelReset")}</button>
        </p>
      )}
      {fehlt !== undefined && <p className="log__levelfehler" role="alert">{fehlt}</p>}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="log__fact">
      <dt className="log__factlabel">{label}</dt>
      <dd className="log__factvalue">{children}</dd>
    </div>
  );
}

// ── Formulare ───────────────────────────────────────────────────────────────

/** Anlegen einer Supplier-Produkt-Beziehung. */
function RelationForm({ articleId, meta, onDone, onError }: {
  articleId: number;
  meta: MetaResponse;
  onDone: (r: SupplierArticle | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [supplierId, setSupplierId] = useState<number | "">("");
  const [number, setNumber] = useState("");
  const [moq, setMoq] = useState("");
  const [multiple, setMultiple] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [origin, setOrigin] = useState<string>("supplier");
  const [method, setMethod] = useState<string>("erfasst");

  async function save(): Promise<void> {
    if (supplierId === "") return;
    try {
      onDone(await api.createSupplierArticle({
        articleId, supplierId: Number(supplierId),
        supplierArticleNumber: number.trim() === "" ? null : number.trim(),
        // Leer bleibt leer: unbekannt wird nicht zu 0.
        moq: moq.trim() === "" ? null : Number(moq),
        orderMultiple: multiple.trim() === "" ? null : Number(multiple),
        leadTimeDays: leadTime.trim() === "" ? null : Number(leadTime),
        origin: origin as any, method: method as any,
      }));
    } catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="log__form">
      <select className="list__select" value={supplierId} aria-label={t("log.supplier")}
        onChange={(e) => setSupplierId(e.target.value === "" ? "" : Number(e.target.value))}>
        <option value="">{t("log.chooseSupplier")}</option>
        {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <input className="list__search" value={number} onChange={(e) => setNumber(e.target.value)}
        aria-label={t("log.supplierArticleNumber")} placeholder={t("log.supplierArticleNumber")} />
      <input className="list__search log__small" value={moq} onChange={(e) => setMoq(e.target.value)}
        aria-label={t("log.moq")} placeholder={t("log.moq")} />
      <input className="list__search log__small" value={multiple} onChange={(e) => setMultiple(e.target.value)}
        aria-label={t("log.orderMultiple")} placeholder={t("log.orderMultiple")} />
      <input className="list__search log__small" value={leadTime} onChange={(e) => setLeadTime(e.target.value)}
        aria-label={t("log.leadTime")} placeholder={t("log.leadTime")} />
      <ProvenanceFields origin={origin} method={method} setOrigin={setOrigin} setMethod={setMethod} />
      <button className="btn btn--primary" onClick={save} disabled={supplierId === ""}>
        {t("common.save")}
      </button>
      <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
    </div>
  );
}

/**
 * Anlegen eines Verpackungsstands.
 *
 * Die Revision steht in KEINEM Feld dieses Formulars. Sie wird vom Server
 * vergeben -- damit kann von hier aus kein bestehender Stand ueberschrieben
 * werden.
 *
 * Die Inner-Box-Felder sind vollstaendig optional. Bleiben sie leer, entsteht
 * ein Verpackungsstand mit ausschliesslich der Masterebene -- ohne Hinweis,
 * ohne Warnung und ohne Nachfrage.
 */
export function VersionForm<R>({ erste, anlegen, meta, onDone, onError }: {
  /** Ob es der erste Stand dieses Traegers ist -- nur dann gibt es den Grund `initial`. */
  erste: boolean;
  /*
   * Wohin der Stand geschrieben wird: an eine Lieferbeziehung oder an ein
   * Produktmodell (0057). Das Formular ist dasselbe -- ein Verpackungsstand
   * hat an beiden Orten dieselben Felder und dieselben Regeln.
   */
  anlegen: (input: CreatePackagingVersionRequest) => Promise<R>;
  meta: MetaResponse;
  onDone: (r: R | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [reason, setReason] = useState(erste ? "initial" : "optimization");
  const [changeNote, setChangeNote] = useState("");
  const [provider, setProvider] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [pl, setPl] = useState(""); const [pw, setPw] = useState(""); const [ph, setPh] = useState("");
  const [origin, setOrigin] = useState<string>("supplier");
  const [method, setMethod] = useState<string>("erfasst");
  const [ml, setMl] = useState(""); const [mw, setMw] = useState(""); const [mh, setMh] = useState("");
  const [mUnits, setMUnits] = useState("1"); const [mWeight, setMWeight] = useState("");
  const [il, setIl] = useState(""); const [iw, setIw] = useState(""); const [ih, setIh] = useState("");
  const [iUnits, setIUnits] = useState(""); const [iWeight, setIWeight] = useState("");
  /*
   * MATERIALGEWICHTE JE EBENE, IN GRAMM.
   *
   * `packaging_level_materials` kennt Pappe, Kunststoff, Holz und Sonstiges.
   * Die API nahm das seit Migration 0012 entgegen, die Maske bot es nicht an
   * -- also stand im gesamten Bestand keine einzige Zeile.
   *
   * Erfasst wird nur, was jemand WEISS. Ein leeres Feld heisst „nicht
   * erfasst", nicht „0 g": die Gewichtsbilanz zieht nur ab, was hier steht,
   * und nennt den Rest weiterhin „nicht zugeordnet". Wer die Kunststofffolie
   * mit 40 g eintraegt, sieht danach 40 g weniger im Unbekannten -- und genau
   * das ist der Zweck.
   */
  const [iMat, setIMat] = useState<Record<string, string>>({});
  const [mMat, setMMat] = useState<Record<string, string>>({});

  // Nur der Grund `initial` ist an Revision 1 gebunden -- alle uebrigen sind
  // in beide Richtungen zulaessig.
  const reasons = meta.packagingChangeReasons.filter((r) => erste || r.code !== "initial");

  async function save(): Promise<void> {
    /* Leere Felder werden nicht mitgeschickt -- ein Material mit 0 g waere
       eine Aussage, und zwar eine falsche. */
    const materialien = (w: Record<string, string>) =>
      Object.entries(w)
        .filter(([, g]) => g.trim() !== "")
        .map(([material, weightG]) => ({ material, weightG }));

    const levels: any[] = [];
    if (il.trim() !== "") {
      levels.push({ type: "inner", lengthMm: il, widthMm: iw, heightMm: ih,
        unitCount: Number(iUnits || "1"),
        grossWeightKg: iWeight.trim() === "" ? null : iWeight,
        materials: materialien(iMat) });
    }
    if (ml.trim() !== "") {
      levels.push({ type: "master", lengthMm: ml, widthMm: mw, heightMm: mh,
        unitCount: Number(mUnits || "1"),
        grossWeightKg: mWeight.trim() === "" ? null : mWeight,
        materials: materialien(mMat) });
    }
    try {
      onDone(await anlegen({
        changeReason: reason as any,
        changeNote: changeNote.trim() === "" ? null : changeNote.trim(),
        provider: provider === "" ? null : provider as any,
        validFrom: validFrom.trim() === "" ? null : validFrom.trim(),
        productLengthMm: pl.trim() === "" ? null : pl,
        productWidthMm: pw.trim() === "" ? null : pw,
        productHeightMm: ph.trim() === "" ? null : ph,
        origin: origin as any, method: method as any,
        levels,
      }));
    } catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="log__form log__form--block">
      <p className="log__formhint">{t("log.newRevisionHint")}</p>

      <div className="log__formrow">
        <select className="list__select" value={reason} aria-label={t("log.changeReason")}
          onChange={(e) => setReason(e.target.value)}>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>{i18n.tCode("packReason", r.code)}</option>
          ))}
        </select>
        <input className="list__search" value={changeNote} onChange={(e) => setChangeNote(e.target.value)}
          aria-label={t("log.changeNote")} placeholder={t("log.changeNote")} />
        <select className="list__select" value={provider} aria-label={t("log.provider")}
          onChange={(e) => setProvider(e.target.value)}>
          <option value="">{t("log.providerUnknown")}</option>
          {meta.packagingProviders.map((p) => (
            <option key={p.code} value={p.code}>{i18n.tCode("packProvider", p.code)}</option>
          ))}
        </select>
        <input className="list__search log__small" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
          aria-label={t("log.validFrom")} placeholder={t("log.datePlaceholder")} />
      </div>

      <div className="log__formrow">
        <span className="log__formlabel">{t("log.productDims")}</span>
        <input className="list__search log__small" value={pl} onChange={(e) => setPl(e.target.value)}
          aria-label={t("log.lengthMm")} placeholder={t("log.lengthMm")} />
        <input className="list__search log__small" value={pw} onChange={(e) => setPw(e.target.value)}
          aria-label={t("log.widthMm")} placeholder={t("log.widthMm")} />
        <input className="list__search log__small" value={ph} onChange={(e) => setPh(e.target.value)}
          aria-label={t("log.heightMm")} placeholder={t("log.heightMm")} />
      </div>

      {/* Optional. Bleibt dieser Block leer, ist der Verpackungsstand
          vollstaendig -- eine Inner Box ist nichts, was hier fehlen kann. */}
      <div className="log__formrow">
        <span className="log__formlabel">{t("log.innerOptional")}</span>
        <input className="list__search log__small" value={il} onChange={(e) => setIl(e.target.value)}
          aria-label={t("log.innerLength")} placeholder={t("log.lengthMm")} />
        <input className="list__search log__small" value={iw} onChange={(e) => setIw(e.target.value)}
          aria-label={t("log.innerWidth")} placeholder={t("log.widthMm")} />
        <input className="list__search log__small" value={ih} onChange={(e) => setIh(e.target.value)}
          aria-label={t("log.innerHeight")} placeholder={t("log.heightMm")} />
        <input className="list__search log__small" value={iUnits} onChange={(e) => setIUnits(e.target.value)}
          aria-label={t("log.innerUnits")} placeholder={t("log.unitCount")} />
        <input className="list__search log__small" value={iWeight} onChange={(e) => setIWeight(e.target.value)}
          aria-label={t("log.innerWeight")} placeholder={t("log.grossWeight")} />
      </div>

      <Materialzeile werte={iMat} setzen={setIMat} meta={meta} i18n={i18n}
        label={t("log.innerMaterials")} />

      <div className="log__formrow">
        <span className="log__formlabel">{t("log.master")}</span>
        <input className="list__search log__small" value={ml} onChange={(e) => setMl(e.target.value)}
          aria-label={t("log.masterLength")} placeholder={t("log.lengthMm")} />
        <input className="list__search log__small" value={mw} onChange={(e) => setMw(e.target.value)}
          aria-label={t("log.masterWidth")} placeholder={t("log.widthMm")} />
        <input className="list__search log__small" value={mh} onChange={(e) => setMh(e.target.value)}
          aria-label={t("log.masterHeight")} placeholder={t("log.heightMm")} />
        <input className="list__search log__small" value={mUnits} onChange={(e) => setMUnits(e.target.value)}
          aria-label={t("log.masterUnits")} placeholder={t("log.unitCount")} />
        <input className="list__search log__small" value={mWeight} onChange={(e) => setMWeight(e.target.value)}
          aria-label={t("log.masterWeight")} placeholder={t("log.grossWeight")} />
      </div>

      <Materialzeile werte={mMat} setzen={setMMat} meta={meta} i18n={i18n}
        label={t("log.masterMaterials")} />

      {/* Es gibt hier kein CBM-Feld. Das Volumen wird berechnet. */}
      <p className="log__formhint">{t("log.cbmComputedHint")}</p>

      <div className="log__formrow">
        <ProvenanceFields origin={origin} method={method} setOrigin={setOrigin} setMethod={setMethod} />
        <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
        <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}

/** Anlegen eines Verpackungsnachweises. */
function TestForm({ versionId, meta, onDone, onError }: {
  versionId: number;
  meta: MetaResponse;
  onDone: (r: SupplierArticle | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [testType, setTestType] = useState(meta.packagingTestTypes[0]?.code ?? "drop_test");
  const [result, setResult] = useState("passed");
  const [performedOn, setPerformedOn] = useState("");
  const [standard, setStandard] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [origin, setOrigin] = useState<string>("supplier");

  async function save(): Promise<void> {
    try {
      onDone(await api.addPackagingTest(versionId, {
        testType: testType as any, result: result as any,
        performedOn: performedOn.trim() === "" ? null : performedOn.trim(),
        origin: origin as any,
        standard: standard.trim() === "" ? null : standard.trim(),
        reference: reference.trim() === "" ? null : reference.trim(),
        note: note.trim() === "" ? null : note.trim(),
      }));
    } catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="log__form">
      <select className="list__select" value={testType} aria-label={t("log.testType")}
        onChange={(e) => setTestType(e.target.value)}>
        {meta.packagingTestTypes.map((x) => (
          <option key={x.code} value={x.code}>{i18n.tCode("packTestType", x.code)}</option>
        ))}
      </select>
      <select className="list__select" value={result} aria-label={t("log.testResult")}
        onChange={(e) => setResult(e.target.value)}>
        <option value="passed">{i18n.tCode("packResult", "passed")}</option>
        <option value="failed">{i18n.tCode("packResult", "failed")}</option>
        <option value="open">{i18n.tCode("packResult", "open")}</option>
      </select>
      <input className="list__search log__small" value={performedOn} onChange={(e) => setPerformedOn(e.target.value)}
        aria-label={t("log.performedOn")} placeholder={t("log.datePlaceholder")} />
      <input className="list__search log__small" value={standard} onChange={(e) => setStandard(e.target.value)}
        aria-label={t("log.testStandard")} placeholder={t("log.testStandard")} />
      {/* Eine fachliche Referenz. Ein Dateipfad wird vom Server abgewiesen. */}
      <input className="list__search" value={reference} onChange={(e) => setReference(e.target.value)}
        aria-label={t("log.testReference")} placeholder={t("log.testReferencePlaceholder")} />
      <input className="list__search" value={note} onChange={(e) => setNote(e.target.value)}
        aria-label={t("log.testNote")} placeholder={t("log.testNote")} />
      <select className="list__select" value={origin} aria-label={t("log.origin")}
        onChange={(e) => setOrigin(e.target.value)}>
        {LOGISTICS_ORIGINS.map((o) => (
          <option key={o} value={o}>{i18n.tCode("textOrigin", o, o)}</option>
        ))}
      </select>
      <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
      <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
    </div>
  );
}

/**
 * Herkunft und Entstehungsweise.
 *
 * Dieselben Wertevorraete wie bei Texten und Merkmalen -- und dieselben
 * Beschriftungen. Es entsteht kein zweiter Wortschatz.
 */
/**
 * Die Materialgewichte einer Verpackungsebene, in Gramm.
 *
 * Ein Feld je Material aus `packaging_materials` -- die Liste kommt aus den
 * Nachschlagedaten, nicht aus dieser Datei. Was leer bleibt, wird nicht
 * gesendet.
 *
 * Warum Gramm und nicht Kilogramm: Verpackungsmaterial wiegt zweistellig bis
 * dreistellig in Gramm. `0,045 kg` liest sich niemand, und in der
 * VerpackungsG-Meldung steht ohnehin Gramm.
 */
function Materialzeile({ werte, setzen, meta, i18n, label }: {
  werte: Record<string, string>;
  setzen: (f: (v: Record<string, string>) => Record<string, string>) => void;
  meta: MetaResponse;
  i18n: I18n;
  label: string;
}): React.JSX.Element {
  return (
    <div className="log__formrow">
      <span className="log__formlabel">{label}</span>
      {meta.packagingMaterials.map((m) => (
        <label key={m.code} className="log__matfeld">
          <span>{i18n.tCode("packMaterial", m.code)}</span>
          <input className="list__search log__small" value={werte[m.code] ?? ""}
            aria-label={i18n.tCode("packMaterial", m.code)}
            placeholder={i18n.t("log.gramShort")}
            onChange={(e) => setzen((v) => ({ ...v, [m.code]: e.target.value }))} />
        </label>
      ))}
    </div>
  );
}

function ProvenanceFields({ origin, method, setOrigin, setMethod }: {
  origin: string; method: string;
  setOrigin: (v: string) => void; setMethod: (v: string) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  return (
    <>
      <select className="list__select" value={origin} aria-label={i18n.t("log.origin")}
        onChange={(e) => setOrigin(e.target.value)}>
        {LOGISTICS_ORIGINS.map((o) => (
          <option key={o} value={o}>{i18n.tCode("textOrigin", o, o)}</option>
        ))}
      </select>
      <select className="list__select" value={method} aria-label={i18n.t("log.method")}
        onChange={(e) => setMethod(e.target.value)}>
        {LOGISTICS_METHODS.map((m) => (
          <option key={m} value={m}>{i18n.tCode("propMethod", m, m)}</option>
        ))}
      </select>
    </>
  );
}
